import { normalizeAuthoringGoalQuery, normalizeCoachRecapQuery } from './authoringQueries';
import type * as Conversation from './types';
/** Typed fetch wrapper for the Tktic JSON API.
 *
 * Single source of `fetch()` calls in the client. Adds:
 *   1. Consistent /api/ base path — feature code never builds URLs.
 *   2. Cookie credentials, so the session cookie flows.
 *   3. Automatic scope injection: routes that need a `team_id`
 *      query param get one from the scope store, transparently.
 *      Callers can override per-request.
 *   4. Non-2xx → `ApiError(status, detail)`. FastAPI serializes
 *      every error as `{ "detail": <value> }` (a string for
 *      `HTTPException(status, "msg")`, an object for
 *      `HTTPException(status, {code, errors})`); `apiFetch`
 *      unwraps that envelope so `ApiError.detail` IS the value —
 *      callers do `typeof err.detail === "string"` or read
 *      `err.detail.errors` directly, never `err.detail.detail`.
 *
 * Why scope injection lives here:
 *   - Forgetting to pass `?team_id=...` on a scoped GET silently
 *     returns a 409 ("Team selection required") or 403 from the
 *     backend. The error message is correct but the failure mode is
 *     wasteful when scope is known. Pushing the param into every
 *     scoped fetch removes a whole class of bugs.
 *   - Callsite spelling stays clean: `getMatches()` not
 *     `getMatches({ team_id: scope.team.id, season_id: scope.season?.id })`.
 */

import { currentScope, useScopeStore } from "@/stores/scope";
import { useAuthStore } from '@/stores/auth';
import { scopeQuery } from "./coachingScope";
import type { CoachingTransportScope } from "./coachingScope";
import type { ResourceDetailResponse, ResourceListQuery, ResourceListResponse, ResourceReadScope,
  ResourceUsesQuery, ResourceUsesResponse, ResourceCommandResult, ResourceRecordingDescriptor,
  SaveResourceRequest, ResourceMetadataRequest, ResourceDuplicateRequest, ResourceArchiveRequest,
  ResourceDeleteRequest, ResourceDeletionResult, ResourcePublishRequest } from './types';
import type { CreateFeedbackWorkspaceRequest, SaveFeedbackWorkspaceRequest, DiscardFeedbackWorkspaceRequest,
  FeedbackAuthoringDraftsQuery, FeedbackAuthoringDraftsResponse,
  FeedbackWorkspaceScope, FeedbackWorkspaceResult, FeedbackWorkspaceDiscardResult, FeedbackWorkspaceResponse } from './types';
import type { CreateFeedbackDraftRequest, UpdateFeedbackDraftRequest, DiscardFeedbackDraftRequest,
  FeedbackDraftScope, FeedbackDraftResult, FeedbackDraftDiscardResult, FeedbackDraftResponse } from './types';
import type { ReviewNewFeedbackRequest, ReviewExistingFeedbackRequest, PublishNewFeedbackRequest,
  PublishExistingFeedbackRequest, FeedbackReviewResult, FeedbackPublicationResult,
  FeedbackPublicationResponse, FeedbackPublicationScope } from './types';
import type { DeleteFeedbackPublicationRequest, FeedbackDeletionScope, FeedbackDeletionResult } from './types';
import type {
  AcceptTeamInviteRequest,
  AcceptTeamInviteResponse,
  AccessibleTeam,
  AccessRequestOptions,
  AccessRequestRow,
  AdminDiagnostics,
  AdminAuthProvidersPayload,
  AdminAuthProvidersUpdateRequest,
  AdminEmailPayload,
  AdminEmailTestRequest,
  AdminEmailTestResponse,
  AdminEmailUpdateRequest,
  AdminFileInventoryResponse,
  AdminLiveConfig,
  ClubLiveConfigPayload,
  ClubLiveStatusPayload,
  ClubOverviewPayload,
  AdminLiveDiagnostics,
  AdminJobsPayload,
  AdminPerformance,
  AdminSettingsPayload,
  AdminStreamsPayload,
  AdminTeamMembership,
  AdminTeamRow,
  AdminUser,
  AdminUserDetail,
  // #908 athlete directory + season teams
  Athlete,
  AthleteCreate,
  AthleteDetail,
  AthleteDirectoryEntry,
  AthleteDirectoryStatusFilter,
  AthleteLinkCreate,
  AthleteMerge,
  AthleteMergeRequest,
  AthleteUpdate,
  AthleteUserLink,
  CarryForwardTeamRequest,
  CarryForwardTeamResult,
  CloseSeasonResult,
  FeedbackAthleteCollection,
  LinkedAthlete,
  RosterAssignmentBulkCreate,
  RosterAssignmentCreate,
  RosterAssignmentEnd,
  RosterAssignmentTransfer,
  RosterAssignmentTransferResult,
  RosterImportCommitRequest,
  AuthIdentitiesPayload,
  AuthModePayload,
  CreateUserRequest,
  EmailVerificationConfirmRequest,
  EmailVerificationRequestResponse,
  LoginRequest,
  LoginResponse,
  PasswordChangeRequest,
  PasswordResetConfirmRequest,
  PasswordResetRequestRequest,
  PasswordResetRequestResponse,
  PatchMeSecurityRequest,
  PatchMeSecurityResponse,
  PatchMeProfileRequest,
  MeSecurityPayload,
  TrustedDeviceSummary,
  UpdateUserRequest,
  CreateAdminMembershipRequest,
  CreateAdminSeasonRequest,
  UpdateAdminSeasonRequest,
  CreateAdminTeamRequest,
  CreateAccessRequestBody,
  CreateTeamInviteRequest,
  CreateTeamInviteResponse,
  CreateClubUserRequest,
  CreateClubUserResponse,
  ClubAdmin,
  ClubMemberCandidate,
  Season,
  TeamInviteRow,
  TeamMembershipRequest,
  UpdateAdminTeamRequest,
  CoachEngagement,
  CoachEngagementFilters,
  CoachReviewBundle,
  CoachUser,
  CoachingClip,
  CoachingNote,
  CoachingPlaylist,
  CreateCoachingClipRequest,
  CreateCoachingNoteRequest,
  CreateCoachingPlaylistRequest,
  CreateMatchRequest,
  CreateMatchSummaryRequest,
  CreatePlayerGoalRequest,
  CreatePlayerGoalReflectionRequest,
  CreatePlayerRequest,
  CreatePlayerUserLinkRequest,
  DevelopmentProfile,
  FeedbackBundle,
  FeedbackReview,
  CreatedShareLink,
  ClubShareLink,
  FoundingClubLeadRequest,
  FoundingClubLeadResponse,
  GuestClaimResponse,
  InviteConsents,
  InviteDescriptor,
  MyConsentsPayload,
  TeamConsentOverview,
  ShareLink,
  Match,
  MatchBySlugResponse,
  MatchSummary,
  MatchesListResponse,
  MePayload,
  NotificationsPayload,
  Player,
  PlayerGoal,
  PlayerGoalReflection,
  PlayerPhotoRequest,
  PublicTeam,
  AppSettingsResponse,
  PublicSettings,
  RegisterRequest,
  RegisterResponse,
  TeamVisibilityResponse,
  RosterImportCommitResult,
  RosterImportPreviewResult,
  SaveScopeResponse,
  TeamSettings,
  TeamSettingsResponse,
  TranscodeProgressMap,
  UnblockStreamRequest,
  UserProfile,
  UpdateMatchRequest,
  UpdateClubSettingsRequest,
  UpdateCoachingClipRequest,
  UpdateCoachingNoteRequest,
  UpdateCoachingPlaylistRequest,
  UpdateMatchSummaryRequest,
  UpdatePlayerGoalRequest,
  UpdatePlayerRequest,
  AdminClubDetail,
  AdminClubRow,
  ClubAdminEntry,
  ClubFlagKey,
  ClubOperatorMeta,
  ClubUsage,
  CreateClubRequest,
  Club,
  ClubStatus,
} from "./types";
import type { operations } from "./api.generated";

export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;

  constructor(status: number, detail: unknown) {
    super(typeof detail === "string" ? detail : `HTTP ${status}`);
    this.status = status;
    this.detail = detail;
  }
}

/** The closed `detail.code` of an API failure, when the server sent one. */
export function apiErrorCode(error: unknown): string | null {
  return error instanceof ApiError && error.detail && typeof error.detail === 'object' && 'code' in error.detail
    ? String(error.detail.code) : null;
}

/** A CAS rejection's authorized `detail.current_version`, when present. */
export function apiCurrentVersion(error: unknown): number | null {
  const value = error instanceof ApiError && error.detail && typeof error.detail === 'object' && 'current_version' in error.detail
    ? error.detail.current_version : null;
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}

type DeleteAdminUserRequest =
  operations["delete_user_api_users__user_id__delete"]["requestBody"]["content"]["application/json"];

/**
 * Best-effort human-readable message for a caught error — for toast
 * descriptions and inline error copy. `ApiError.message` is already
 * the unwrapped FastAPI `detail` string (or `HTTP <status>` when the
 * detail was structured); a plain `Error` yields its message; an
 * unknown throw falls back to a generic line. Previously each page
 * hand-rolled this `instanceof` ladder.
 */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong. Please try again.";
}

/** 403 `mfa_required` → the in-app AAL2 step-up (#358): route to
 * `/login?aal=aal2&next=<here>`, where `KratosStepUpPanel` runs a
 * second-factor Kratos login flow and re-exchanges the app session.
 * A full navigation (not SPA route) on purpose — the failed call's
 * surface may hold half-applied state, and the step-up return
 * reloads it cleanly. The ApiError still throws to the caller, which
 * is fine: the page is already navigating away. */
let mfaStepUpRedirect: ((target: string) => void) | null = null;

function redirectToMfaStepUp(status: number, detail: unknown): void {
  if (status !== 403 || !detail || typeof detail !== "object") return;
  if ((detail as { code?: unknown }).code !== "mfa_required") return;
  const here = `${window.location.pathname}${window.location.search}`;
  const target = `/login?aal=aal2&next=${encodeURIComponent(here)}`;
  if (mfaStepUpRedirect) {
    mfaStepUpRedirect(target);
    return;
  }
  window.location.assign(target);
}

export function configureMfaStepUpRedirectForTest(
  redirect: ((target: string) => void) | null,
): void {
  mfaStepUpRedirect = redirect;
}

export type ApiOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  /** Per-request scope override. Pass `false` to suppress the default
   * scope injection (admin endpoints that explicitly span tenants).
   * Pass an object to override the team/season for this one call. */
  scope?: CoachingTransportScope | false;
  /** Extra query params, in addition to scope. */
  query?: Record<string, string | readonly string[] | number | boolean | null | undefined>;
};

/** Resource reads require resolved scope; historical pins never use an implicit latest URL. */
export type ResourceReadOptions = { scope: ResourceReadScope; signal?: AbortSignal };

async function resourceFetch<T>(path: string, options: ResourceReadOptions,
  query?: ApiOptions['query']): Promise<T> {
  const matches = () => {
    const auth = useAuthStore.getState();
    return !auth.hydrating && auth.user?.id === options.scope.actorId
      && auth.currentClub?.id === options.scope.clubId;
  };
  if (!matches()) throw new DOMException('Resource identity changed', 'AbortError');
  let departed = false;
  const unsubscribe = useAuthStore.subscribe(() => { if (!matches()) departed = true; });
  const token = getAuthToken();
  try {
    const value = await apiFetch<T>(path, { ...resourceReadOptions(options), query });
    if (departed || !matches() || token !== getAuthToken() || options.signal?.aborted) {
      throw new DOMException('Resource identity changed', 'AbortError');
    }
    return value;
  } catch (error) {
    // An old 401 must not expire a newly authenticated account via QueryCache.
    if (departed || !matches() || token !== getAuthToken() || options.signal?.aborted) {
      throw new DOMException('Resource identity changed', 'AbortError');
    }
    throw error;
  } finally {
    unsubscribe();
  }
}

function resourceReadOptions(options: ResourceReadOptions): ApiOptions {
  const { actorId, clubId, teamId, seasonId } = options.scope;
  if (!actorId || !clubId || !teamId || seasonId === '') throw new RangeError('invalid_resource_scope');
  return { signal: options.signal, cache: 'no-store', scope: {
    teamId, seasonId, seasonMode: seasonId === null ? 'unseasoned' : 'current',
  } };
}

export function normalizeResourceListQuery(query: ResourceListQuery = {}) {
  return { search: (query.search ?? '').trim(), kind: query.kind ?? null, match_id: query.match_id ?? null,
    without_match: query.without_match ?? false, source_date: query.source_date ?? null,
    archive: query.archive ?? 'all', visible_use: query.visible_use ?? 'all', publication: query.publication ?? 'all',
    sort: query.sort ?? 'updated_desc', limit: query.limit ?? 20, cursor: query.cursor ?? null };
}

export function normalizeResourceUsesQuery(query: ResourceUsesQuery = {}) {
  return { publication: query.publication ?? 'all', limit: query.limit ?? 20, cursor: query.cursor ?? null };
}

export function listCoachResources(query: ResourceListQuery, options: ResourceReadOptions): Promise<ResourceListResponse> {
  return resourceFetch(BASE_RESOURCE_PATH, options, normalizeResourceListQuery(query));
}

const BASE_RESOURCE_PATH = '/api/coach/resources';

export function getCoachResource(resourceId: string, options: ResourceReadOptions): Promise<ResourceDetailResponse> {
  return resourceFetch(`${BASE_RESOURCE_PATH}/${encodeURIComponent(resourceId)}`, options);
}

export function getCoachResourceRevision(pin: { resource_id: string; revision_id: string }, options: ResourceReadOptions): Promise<ResourceDetailResponse> {
  return resourceFetch(`${BASE_RESOURCE_PATH}/${encodeURIComponent(pin.resource_id)}/revisions/${encodeURIComponent(pin.revision_id)}`,
    options);
}

export function listCoachResourceUses(resourceId: string, query: ResourceUsesQuery, options: ResourceReadOptions): Promise<ResourceUsesResponse> {
  return resourceFetch(`${BASE_RESOURCE_PATH}/${encodeURIComponent(resourceId)}`, options,
    { projection: 'uses', ...normalizeResourceUsesQuery(query) });
}

/** Current resolved authority for a command; the server rechecks independently. */
export function isCurrentResourceCommandScope(scope: ResourceReadScope): boolean {
  return isCurrentFeedbackPublicationReadScope(scope)
    && useScopeStore.getState().active?.capabilities.includes('coach_object:write') === true;
}

/** Current publication reads need coaching read capability, independently of writing. */
export function isCurrentFeedbackPublicationReadScope(scope: ResourceReadScope): boolean {
  const auth = useAuthStore.getState();
  const current = useScopeStore.getState();
  return !auth.hydrating && auth.user?.id === scope.actorId && auth.currentClub?.id === scope.clubId
    && auth.currentClub.status === 'active'
    && !current.selectionRequired && current.teamId === scope.teamId && current.seasonId === scope.seasonId
    && current.active?.team.id === scope.teamId && (current.active?.season?.id ?? null) === scope.seasonId
    && current.active.capabilities.includes('coach_object:read');
}

async function resourceCommandFetch<T>(path: string, options: ResourceReadOptions,
  request: Pick<ApiOptions, 'method' | 'body' | 'query'>, readOnly = false): Promise<T> {
  const captured = { ...options, scope: { ...options.scope } };
  const scoped = resourceReadOptions(captured);
  const authoringReadAuthority = path === '/api/coach/feedback-goals' ? feedbackConversationAuthorityFingerprint() : null;
  const matches = () => (authoringReadAuthority === null || authoringReadAuthority === feedbackConversationAuthorityFingerprint())
    && (readOnly ? isCurrentFeedbackPublicationReadScope(captured.scope) : isCurrentResourceCommandScope(captured.scope));
  if (!matches() || captured.signal?.aborted) throw new DOMException('Resource context changed', 'AbortError');
  let departed = false;
  const observe = () => { if (!matches()) departed = true; };
  const unsubscribeAuth = useAuthStore.subscribe(observe);
  const unsubscribeScope = useScopeStore.subscribe(observe);
  const token = getAuthToken();
  const tokenGeneration = authTokenGeneration;
  try {
    const value = await apiFetch<T>(path, { ...scoped, ...request });
    if (departed || !matches() || token !== getAuthToken() || tokenGeneration !== authTokenGeneration || captured.signal?.aborted) {
      throw new DOMException('Resource context changed', 'AbortError');
    }
    return value;
  } catch (error) {
    // Fence old errors as well as successes, including leave-and-return.
    if (departed || !matches() || token !== getAuthToken() || tokenGeneration !== authTokenGeneration || captured.signal?.aborted) {
      throw new DOMException('Resource context changed', 'AbortError');
    }
    if (path === '/api/coach/feedback-goals' && error instanceof ApiError && [401,403,404].includes(error.status)) {
      const cache = await import('./queryClient');
      if (departed || !matches() || token !== getAuthToken() || tokenGeneration !== authTokenGeneration || captured.signal?.aborted) {
        throw new DOMException('Resource context changed', 'AbortError');
      }
      if (error.status === 401) cache.applyAuthExpiredBoundary();
      else await cache.removeFeedbackGoalChoices(captured.scope);
    }
    throw error;
  } finally {
    unsubscribeAuth(); unsubscribeScope();
  }
}

export function getCoachResourceRecording(matchId: string, slot: ResourceRecordingDescriptor['slot'],
  options: ResourceReadOptions): Promise<ResourceRecordingDescriptor> {
  return resourceCommandFetch(`${BASE_RESOURCE_PATH}/recording-source`, options,
    { query: { match_id: matchId, slot } });
}

export function createCoachResource(body: SaveResourceRequest, options: ResourceReadOptions): Promise<ResourceCommandResult> {
  return resourceCommandFetch(BASE_RESOURCE_PATH, options, { method: 'POST', body });
}

export function saveCoachResourceRevision(resourceId: string, body: SaveResourceRequest,
  options: ResourceReadOptions): Promise<ResourceCommandResult> {
  return resourceCommandFetch(`${BASE_RESOURCE_PATH}/${encodeURIComponent(resourceId)}/revisions`, options, { method: 'POST', body });
}

export function updateCoachResourceMetadata(resourceId: string, body: ResourceMetadataRequest,
  options: ResourceReadOptions): Promise<ResourceCommandResult> {
  return resourceCommandFetch(`${BASE_RESOURCE_PATH}/${encodeURIComponent(resourceId)}/metadata`, options, { method: 'PATCH', body });
}

export function duplicateCoachResource(resourceId: string, body: ResourceDuplicateRequest,
  options: ResourceReadOptions): Promise<ResourceCommandResult> {
  return resourceCommandFetch(`${BASE_RESOURCE_PATH}/${encodeURIComponent(resourceId)}/duplicate`, options, { method: 'POST', body });
}

export function archiveCoachResource(resourceId: string, body: ResourceArchiveRequest,
  options: ResourceReadOptions): Promise<ResourceCommandResult> {
  return resourceCommandFetch(`${BASE_RESOURCE_PATH}/${encodeURIComponent(resourceId)}/archive`, options, { method: 'POST', body });
}

export function publishCoachResource(resourceId: string, body: ResourcePublishRequest,
  options: ResourceReadOptions): Promise<ResourceCommandResult> {
  return resourceCommandFetch(`${BASE_RESOURCE_PATH}/${encodeURIComponent(resourceId)}/publish`, options, { method: 'POST', body });
}

export function deleteCoachResource(resourceId: string, body: ResourceDeleteRequest,
  options: ResourceReadOptions): Promise<ResourceDeletionResult> {
  return resourceCommandFetch(`${BASE_RESOURCE_PATH}/${encodeURIComponent(resourceId)}`, options, { method: 'DELETE', body });
}

/** Workspace reads and writes share the exact resolved coach departure fence. */
export type FeedbackWorkspaceOptions = { scope: FeedbackWorkspaceScope; signal?: AbortSignal };
export const isCurrentFeedbackWorkspaceScope = isCurrentResourceCommandScope;
const BASE_WORKSPACE_PATH = '/api/coach/feedback-workspaces';

export function listFeedbackAuthoringDrafts(query: FeedbackAuthoringDraftsQuery,
  options: FeedbackWorkspaceOptions): Promise<FeedbackAuthoringDraftsResponse> {
  return resourceCommandFetch(`${BASE_WORKSPACE_PATH}/drafts`, options, { query });
}

export function createFeedbackWorkspace(body: CreateFeedbackWorkspaceRequest,
  options: FeedbackWorkspaceOptions): Promise<FeedbackWorkspaceResult> {
  return resourceCommandFetch(BASE_WORKSPACE_PATH, options, { method: 'POST', body });
}

export function getFeedbackWorkspace(workspaceId: string,
  options: FeedbackWorkspaceOptions): Promise<FeedbackWorkspaceResponse> {
  return resourceCommandFetch(`${BASE_WORKSPACE_PATH}/${encodeURIComponent(workspaceId)}`, options, {});
}

export function saveFeedbackWorkspace(workspaceId: string, body: SaveFeedbackWorkspaceRequest,
  options: FeedbackWorkspaceOptions): Promise<FeedbackWorkspaceResult> {
  return resourceCommandFetch(`${BASE_WORKSPACE_PATH}/${encodeURIComponent(workspaceId)}`, options, { method: 'PUT', body });
}

export function discardFeedbackWorkspace(workspaceId: string, body: DiscardFeedbackWorkspaceRequest,
  options: FeedbackWorkspaceOptions): Promise<FeedbackWorkspaceDiscardResult> {
  return resourceCommandFetch(`${BASE_WORKSPACE_PATH}/${encodeURIComponent(workspaceId)}`, options, { method: 'DELETE', body });
}

/** Canonical drafts use the same current-coach, host and departure fence. */
export type FeedbackDraftOptions = { scope: FeedbackDraftScope; signal?: AbortSignal };
export const isCurrentFeedbackDraftScope = isCurrentResourceCommandScope;
const BASE_DRAFT_PATH = '/api/coach/feedback';

export function createFeedbackDraft(body: CreateFeedbackDraftRequest,
  options: FeedbackDraftOptions): Promise<FeedbackDraftResult> {
  return resourceCommandFetch(BASE_DRAFT_PATH, options, { method: 'POST', body });
}
export function getFeedbackDraft(feedbackId: string,
  options: FeedbackDraftOptions): Promise<FeedbackDraftResponse> {
  return resourceCommandFetch(`${BASE_DRAFT_PATH}/${encodeURIComponent(feedbackId)}`, options, {});
}
export function saveFeedbackDraft(feedbackId: string, body: UpdateFeedbackDraftRequest,
  options: FeedbackDraftOptions): Promise<FeedbackDraftResult> {
  return resourceCommandFetch(`${BASE_DRAFT_PATH}/${encodeURIComponent(feedbackId)}`, options, { method: 'PATCH', body });
}
export function discardFeedbackDraft(feedbackId: string, body: DiscardFeedbackDraftRequest,
  options: FeedbackDraftOptions): Promise<FeedbackDraftDiscardResult> {
  return resourceCommandFetch(`${BASE_DRAFT_PATH}/${encodeURIComponent(feedbackId)}`, options, { method: 'DELETE', body });
}

export type FeedbackPublicationOptions = { scope: FeedbackPublicationScope; signal?: AbortSignal };
export const isCurrentFeedbackPublicationScope = isCurrentResourceCommandScope;
export function reviewNewFeedback(body: ReviewNewFeedbackRequest, options: FeedbackPublicationOptions): Promise<FeedbackReviewResult> {
  return resourceCommandFetch(`${BASE_DRAFT_PATH}/review`, options, { method: 'POST', body });
}
export function reviewExistingFeedback(feedbackId: string, body: ReviewExistingFeedbackRequest,
  options: FeedbackPublicationOptions): Promise<FeedbackReviewResult> {
  return resourceCommandFetch(`${BASE_DRAFT_PATH}/${encodeURIComponent(feedbackId)}/review`, options, { method: 'POST', body });
}
export function publishNewFeedback(body: PublishNewFeedbackRequest, options: FeedbackPublicationOptions): Promise<FeedbackPublicationResult> {
  return resourceCommandFetch(`${BASE_DRAFT_PATH}/publish`, options, { method: 'POST', body });
}
export function publishExistingFeedback(feedbackId: string, body: PublishExistingFeedbackRequest,
  options: FeedbackPublicationOptions): Promise<FeedbackPublicationResult> {
  return resourceCommandFetch(`${BASE_DRAFT_PATH}/${encodeURIComponent(feedbackId)}/publish`, options, { method: 'POST', body });
}
export function getFeedbackPublication(feedbackId: string, options: FeedbackPublicationOptions): Promise<FeedbackPublicationResponse> {
  return resourceCommandFetch(`${BASE_DRAFT_PATH}/${encodeURIComponent(feedbackId)}/publication`, options, {}, true);
}

export function normalizeFeedbackGoalChoices(query: Conversation.FeedbackGoalChoicesQuery) {
  return normalizeAuthoringGoalQuery(query);
}
export function listFeedbackGoalChoices(query: Conversation.FeedbackGoalChoicesQuery,
  options: FeedbackWorkspaceOptions): Promise<Conversation.FeedbackGoalChoicesResponse> {
  return resourceCommandFetch('/api/coach/feedback-goals',options,{ query:normalizeFeedbackGoalChoices(query) },true);
}


export type FeedbackDeletionOptions = { scope: FeedbackDeletionScope; signal?: AbortSignal };
export const isCurrentFeedbackDeletionScope = isCurrentResourceCommandScope;
export function deleteFeedbackPublication(feedbackId: string, body: DeleteFeedbackPublicationRequest,
  options: FeedbackDeletionOptions): Promise<FeedbackDeletionResult> {
  return resourceCommandFetch(`${BASE_DRAFT_PATH}/${encodeURIComponent(feedbackId)}/publication`, options, { method: 'DELETE', body });
}

/** Routes that should NEVER receive auto-injected scope params, even
 * when scope is set. Includes auth, public settings, global-admin
 * endpoints, and account self-service. Add a path here when you
 * confirm the backend ignores `?team_id=` for that route.
 *
 * Match is prefix-based against the URL path; `/api/admin/teams`
 * matches `/api/admin/teams` AND `/api/admin/teams/{id}`. */
const UNSCOPED_PATH_PREFIXES = [
  "/api/login",
  "/api/logout",
  "/api/me", // also covers /api/me/profile, /api/me/password, etc.
  "/api/settings", // covers /api/settings and /api/settings/public
  "/api/admin/", // global-admin routes
  "/api/super-admin/", // platform super-admin (club CRUD) — never scoped
  "/api/club/", // per-club settings — club comes from Host, not params
  "/api/users", // global-admin
  "/api/jobs", // job queue
  "/api/auth/", // password reset, email verification
  "/api/app-assets/",
];

function shouldInjectScope(path: string): boolean {
  return !UNSCOPED_PATH_PREFIXES.some((p) =>
    p.endsWith("/")
      ? path.startsWith(p)
      : path === p || path.startsWith(`${p}/`),
  );
}

function buildUrl(path: string, opts: ApiOptions): string {
  const params = new URLSearchParams();
  // Auto-scope unless the caller opted out OR the path is unscoped.
  if (opts.scope !== false && shouldInjectScope(path)) {
    const explicit = opts.scope ?? currentScope();
    for (const [name, value] of Object.entries(scopeQuery(explicit))) params.set(name, value);
  }
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v === null || v === undefined) continue;
    if (Array.isArray(v)) {
      params.delete(k);
      for (const item of v) params.append(k,item);
    } else params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/** Storage key for the legacy bearer token.
 *
 * Dual-mode rollout still lets migrated operators fall back to the legacy
 * username/password flow, so the token helpers remain until cutover is
 * complete. Once `/api/auth/mode` reports Kratos-only mode
 * (`legacy_enabled: false`), the frontend clears this storage key and stops
 * attaching bearer tokens to normal requests. Kratos-backed sessions flow
 * through the HttpOnly app-session cookie only.
 */
const AUTH_TOKEN_STORAGE_KEY = "tktic_auth_token";
const CSRF_COOKIE_NAME = "tktic_csrf";
const CSRF_HEADER_NAME = "X-Tktic-CSRF";
let browserBearerAuthEnabled = true;
// A token can change and return while a request is pending. Value equality alone
// would accept its stale result; generation is in-memory and stores no credential.
let authTokenGeneration = 0;
const authTokenObservers = new Set<() => void>();
export function observeAuthTokenChanges(observer: () => void): () => void {
  authTokenObservers.add(observer);
  return () => { authTokenObservers.delete(observer); };
}

export function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setAuthToken(token: string | null): void {
  const changed = token !== getAuthToken();
  if (changed) authTokenGeneration += 1;
  if (typeof window === "undefined") return;
  try {
    if (token) {
      window.localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
    } else {
      window.localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    }
  } catch {
    // localStorage quota / private-browsing fallback: drop silently.
    // The next request will 401 and the caller can surface a re-
    // login prompt.
  }
  if (changed) for (const observe of authTokenObservers) {
    try { observe(); } catch { /* One consumer must not prevent other owners retiring. */ }
  }
}

export function getAuthTokenForRequest(): string | null {
  if (!browserBearerAuthEnabled) return null;
  return getAuthToken();
}

function isUnsafeMethod(method: string | undefined): boolean {
  return ["DELETE", "PATCH", "POST", "PUT"].includes(
    (method ?? "GET").toUpperCase(),
  );
}

/** DEV-only club override.
 *
 * Single-localhost Vite has no subdomains, so a developer can't exercise
 * Host→club routing the way production does. When `VITE_DEV_CLUB_OVERRIDE`
 * is set, the `DevClubSwitcher` writes the chosen club slug here and we
 * attach it as `X-Tktic-Club-Override` on every request. The backend honors
 * the header only when `TKTIC_CLUB_DEV_OVERRIDE` is truthy and silently
 * ignores it otherwise — the two gates mirror each other exactly, so this
 * is a no-op in production (and stripped from prod bundles by the
 * `import.meta.env.DEV` guard, which tree-shakes the whole branch).
 *
 * The slug is persisted in localStorage so it survives the full-document
 * reload the switcher triggers (the backend resolves the club per-request
 * from the header, so the choice must outlive the navigation).
 */
/** DEV-only club override.
 *
 * Single-localhost Vite has no subdomains, so a developer can't exercise the
 * production Host→club routing path. When `VITE_DEV_CLUB_OVERRIDE` is set, the
 * `DevClubSwitcher` writes the chosen club slug here and every request carries
 * it as `X-Tktic-Club-Override`. The backend honors that header only when its
 * own `TKTIC_CLUB_DEV_OVERRIDE` gate is on — the two mirror each other, so this
 * is a no-op in production AND stripped from prod bundles (the `import.meta.env.DEV`
 * guard below tree-shakes the whole branch). The slug lives in localStorage so
 * it survives the full-document reload the switcher triggers.
 */
export function getDevClubOverride(): string | null {
  // `import.meta.env.DEV` is a build-time literal: in a production build Vite
  // inlines it as `false`, so this whole body — and the string constants it
  // references — is statically dead and rollup eliminates it from the bundle.
  // The early `if (!DEV)` guard (rather than a runtime helper) is what makes
  // the elimination possible; keep it as the first statement.
  if (!import.meta.env.DEV) return null;
  if (
    !import.meta.env.VITE_DEV_CLUB_OVERRIDE ||
    typeof window === "undefined"
  ) {
    return null;
  }
  try {
    return window.localStorage.getItem("tktic_dev_club_override");
  } catch {
    return null;
  }
}

export function setDevClubOverride(slug: string | null): void {
  if (!import.meta.env.DEV) return;
  if (
    !import.meta.env.VITE_DEV_CLUB_OVERRIDE ||
    typeof window === "undefined"
  ) {
    return;
  }
  try {
    if (slug) {
      window.localStorage.setItem("tktic_dev_club_override", slug);
    } else {
      window.localStorage.removeItem("tktic_dev_club_override");
    }
  } catch {
    // best-effort; a failed write just means no override this session
  }
}

/** DEV-only ADMIN-HOST override.
 *
 * The sibling of the club override above, for the other end of the host
 * taxonomy. The operator console is `admin.<apex>`-only, but single-localhost
 * has no subdomains and bare localhost classifies as `apex` — so without this,
 * `/super-admin` would be unreachable in dev and in the Playwright stack.
 * When set, `hostKind()` reports "admin" for hosts that would otherwise be
 * `apex` (a real club subdomain is left alone).
 *
 * Purely client-side classification: unlike the club override there is no
 * paired request header, because the backend already treats "no host club" as
 * the operator context — there is nothing to tell it. Same `import.meta.env.DEV`
 * early guard as `getDevClubOverride`, so the branch is statically dead and
 * rollup strips it from production bundles; keep the guard first.
 */
export function getDevAdminHostOverride(): boolean {
  if (!import.meta.env.DEV) return false;
  if (
    !import.meta.env.VITE_DEV_ADMIN_HOST_OVERRIDE ||
    typeof window === "undefined"
  ) {
    return false;
  }
  try {
    return window.localStorage.getItem("tktic_dev_admin_host") === "1";
  } catch {
    return false;
  }
}

export function devClubOverrideHeader(): Record<string, string> {
  if (!import.meta.env.DEV) return {};
  const slug = getDevClubOverride();
  return slug ? { "X-Tktic-Club-Override": slug } : {};
}

export function getCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${CSRF_COOKIE_NAME}=`;
  // A browser can retain duplicate CSRF cookie names at different scopes
  // during a domain transition. Starlette's cookie parser resolves duplicate
  // names last-wins, so mirror that rule when building the double-submit
  // header or the header and Cookie values can disagree.
  const found = document.cookie
    .split(";")
    .map((part) => part.trim())
    .filter((part) => part.startsWith(prefix))
    .pop();
  if (!found) return null;
  try {
    return decodeURIComponent(found.slice(prefix.length));
  } catch {
    return found.slice(prefix.length);
  }
}

export function csrfHeaderForRequest(
  method: string | undefined,
): Record<string, string> {
  if (!isUnsafeMethod(method)) return {};
  const token = getCsrfToken();
  return token ? { [CSRF_HEADER_NAME]: token } : {};
}

function rememberAuthMode(payload: AuthModePayload): AuthModePayload {
  browserBearerAuthEnabled = payload.legacy_enabled;
  if (!payload.legacy_enabled) {
    setAuthToken(null);
  }
  return payload;
}

export function resetAuthModeMemoryForTest(): void {
  browserBearerAuthEnabled = true;
}

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiOptions = {},
): Promise<T> {
  const { body, headers, scope: _scope, query: _query, ...rest } = options;
  const token = getAuthTokenForRequest();
  const init: RequestInit = {
    credentials: "include",
    ...rest,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...csrfHeaderForRequest(rest.method),
      ...devClubOverrideHeader(),
      ...(headers ?? {}),
    },
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  const url = buildUrl(path, options);
  const res = await fetch(url, init);
  if (res.status === 204 || res.status === 205) {
    return undefined as T;
  }
  const text = await res.text();
  let parsed: unknown = text;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // leave as raw text — caller can string-handle if needed
    }
  }
  if (!res.ok) {
    // FastAPI wraps every error body as `{ detail: <value> }`.
    // Unwrap that one envelope so `ApiError.detail` is the actual
    // value — a string for `HTTPException(status, "msg")`, an
    // object for `HTTPException(status, {code, errors})`. A body
    // that isn't shaped that way (raw text, a non-FastAPI error)
    // passes through unchanged.
    const detail =
      parsed && typeof parsed === "object" && "detail" in parsed
        ? (parsed as { detail: unknown }).detail
        : parsed;
    redirectToMfaStepUp(res.status, detail);
    throw new ApiError(res.status, detail);
  }
  return parsed as T;
}

async function apiFormFetch<T = unknown>(
  path: string,
  formData: FormData,
  options: ApiOptions = {},
): Promise<T> {
  const { headers, scope: _scope, query: _query, ...rest } = options;
  const token = getAuthTokenForRequest();
  const res = await fetch(buildUrl(path, options), {
    method: "POST",
    credentials: "include",
    ...rest,
    body: formData,
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...csrfHeaderForRequest(rest.method ?? "POST"),
      ...devClubOverrideHeader(),
      ...(headers ?? {}),
    },
  });
  const text = await res.text();
  let parsed: unknown = text;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // leave as text
    }
  }
  if (!res.ok) {
    const detail =
      parsed && typeof parsed === "object" && "detail" in parsed
        ? (parsed as { detail: unknown }).detail
        : parsed;
    redirectToMfaStepUp(res.status, detail);
    throw new ApiError(res.status, detail);
  }
  return parsed as T;
}

// ---------------------------------------------------------------------------
// Typed verbs (M2 surface)
// ---------------------------------------------------------------------------

/** Public boot payload — anonymous, no scope.
 *
 * Narrow shell-boot payload (branding strings + asset URLs + the runtime
 * apex domain, #567). Use `getAppSettings` (below) for the full branding
 * dictionary the public viewer surfaces need. */
export function getPublicSettings(): Promise<PublicSettings> {
  return apiFetch<PublicSettings>("/api/settings/public");
}

/** Full public settings dictionary — anonymous, no scope.
 *
 * Used by M11 public surfaces (season view, game view, live view)
 * to read branding labels, the `live_enabled` / `downloads_enabled`
 * flags, the offline live message, the season title + intro, etc.
 * Distinct from `getPublicSettings` which is the narrow shell-boot
 * payload. The backend strips admin-only fields before
 * returning so this is safe for an anonymous fetch.
 *
 * Boolean settings arrive as `"0"` / `"1"` strings — coerce via
 * `coerceBool` from `@/lib/coerce`. */
export function getAppSettings(): Promise<AppSettingsResponse> {
  return apiFetch<AppSettingsResponse>("/api/settings", { scope: false });
}

export function createFoundingClubLead(
  input: FoundingClubLeadRequest,
): Promise<FoundingClubLeadResponse> {
  return apiFetch<FoundingClubLeadResponse>(
    "/api/marketing/founding-club-leads",
    {
      method: "POST",
      body: input,
      scope: false,
    },
  );
}

// VOD heartbeat: see `useVodHeartbeat` in `features/playback/`. It
// builds the URL directly rather than going through this module so
// the kill-propagation 403 handler stays in one place. No verb
// re-exported here.

/** Auth + scope summary. 401 surfaces as `ApiError`. */
export function getMe(): Promise<MePayload> {
  return apiFetch<MePayload>("/api/me");
}

/** Save active team/season. `season_id` is optional — when omitted, the
 * backend's `resolve_scope` uses the team's own season (season teams), else
 * the season already saved for that team, else the club's pinned default
 * season. Returns the new /api/me payload. */
export function saveScope(input: {
  team_id: string;
  season_id?: string;
}): Promise<SaveScopeResponse> {
  return apiFetch<SaveScopeResponse>("/api/me/scope", {
    method: "PUT",
    body: input,
    scope: false, // path is /api/me/* — already unscoped, but explicit
  });
}

/** `GET /api/me/athletes` — the signed-in account's linked athletes on this
 * club host (self / guardian / family, #908 D4) with the assignments the
 * account still holds a projected membership for (D5). Club host only —
 * 404 on the apex. Not team-scoped, so scope injection is suppressed. */
export function getMyAthletes(): Promise<LinkedAthlete[]> {
  return apiFetch<LinkedAthlete[]>("/api/me/athletes", { scope: false });
}

/** Read the complete authorized list while preserving the backend's total.
 * Snapshot scope once: a later active-team switch cannot redirect page two. */
async function allMatchPages(options: ApiOptions): Promise<Match[]> {
  const matches = new Map<string, Match>();
  for (let page = 1; ; page++) {
    const result = await apiFetch<Match[] | MatchesListResponse>(
      "/api/matches",
      { ...options, query: { ...options.query, page, limit: 200 } },
    );
    if (Array.isArray(result)) return result;
    result.matches.forEach((match) => matches.set(match.id, match));
    if (page * 200 >= result.total) return [...matches.values()];
    if (!result.matches.length)
      throw new Error("The match list changed while loading. Please retry.");
  }
}

/** Matches list for the active TEAM — across ALL its seasons.
 *
 * Scope: `team_id` is injected from the store, but `season_id` is
 * explicitly suppressed (`seasonId: null`). The backend treats
 * `?team_id=X` (no season) as "all of team X's matches" (tktic#8).
 * The Coach Review match picker and goal sources need that:
 * they must reach matches in any season, not just the active one.
 * Coach > Matches is the season-scoped exception (`getSeasonMatches`);
 * don't re-narrow this verb.
 */
export function getMatches(): Promise<Match[]> {
  const { teamId } = currentScope();
  return allMatchPages({ scope: { teamId, seasonId: null } });
}

/** Matches list for the active team in the ACTIVE season only. Coach >
 * Matches follows the season picker like the other coach tabs; a legacy
 * (unseasoned, #908) team keeps matches from several seasons.
 *
 * No active season reads as an empty season, never as every season: the
 * server resolves a season whenever the club has one, and a club with
 * none has no seasoned matches to list. */
export function getSeasonMatches(): Promise<Match[]> {
  const { teamId, seasonId } = currentScope();
  if (!seasonId) return Promise.resolve([]);
  return allMatchPages({ scope: { teamId, seasonId } });
}

// ---------------------------------------------------------------------------
// M12a — Multi-team public surface
// ---------------------------------------------------------------------------

/** `GET /api/teams/public` — anonymous list of every public team. */
export function getPublicTeams(): Promise<PublicTeam[]> {
  return apiFetch<PublicTeam[]>("/api/teams/public", { scope: false });
}

/** `GET /api/teams/accessible` — authenticated, host-club-scoped directory.
 * Includes private teams only when the server resolves membership or
 * operational authority for the caller. This is private account data and its
 * query cache must be removed at the auth boundary. */
export function getAccessibleTeams(): Promise<AccessibleTeam[]> {
  return apiFetch<AccessibleTeam[]>("/api/teams/accessible", { scope: false });
}

/** `GET /api/teams/{slug}/public` — viewer-safe team metadata.
 * Public teams are anonymous-readable; private teams require member,
 * operator, or claimed-guest access and otherwise use the same 404 shape. */
export function getPublicTeam(slug: string): Promise<PublicTeam> {
  return apiFetch<PublicTeam>(`/api/teams/${encodeURIComponent(slug)}/public`, {
    scope: false,
  });
}

/** Resolve a single viewer-visible match and bounded same-team neighbors. */
export function getMatchBySlug(slug: string): Promise<MatchBySlugResponse> {
  return apiFetch<MatchBySlugResponse>(
    `/api/matches/by-slug/${encodeURIComponent(slug)}`,
    { scope: false },
  );
}

/** `PATCH /api/teams/{id}/visibility` — toggle the team's public flag.
 * Requires team_settings:manage on the target team (team_admin / global_admin). */
export function setTeamVisibility(
  teamId: string,
  isPublic: boolean,
): Promise<TeamVisibilityResponse> {
  return apiFetch<TeamVisibilityResponse>(
    `/api/teams/${encodeURIComponent(teamId)}/visibility`,
    {
      method: "PATCH",
      body: { public: isPublic },
      // `/api/teams/{id}/visibility` is team-scoped via the path param;
      // the backend resolves the team from the URL, so injecting scope
      // params would be redundant noise.
      scope: false,
    },
  );
}

// ---------------------------------------------------------------------------
// M12c — per-team live streaming
// ---------------------------------------------------------------------------

/** Response shape from a per-team `/api/live/status?team_slug=…` call. */
export type TeamLiveStatus = {
  enabled: boolean;
  team_slug?: string;
  active: boolean;
  ready: boolean;
  reachable?: boolean;
};

/** Response shape from `POST /api/teams/{id}/stream-key/rotate`. The key
 * surfaces ONCE per rotation; never stash it long-term in client state. */
export type TeamStreamKeyResponse = {
  id: string;
  slug: string;
  name: string;
  stream_key: string;
  stream_path: string;
  last_rotated_at: string | null;
  last_rotated_by: string | null;
};

/** Response shape from `GET /api/teams/{id}/stream-key/audit`. */
export type TeamStreamKeyAudit = {
  last_rotated_at: string | null;
  last_rotated_by: string | null;
};

/** `GET /api/live/status?team_slug=<slug>` — per-team live state for the
 * team page's live CTA + the team live shell. */
export function getLiveStatusForTeam(slug: string): Promise<TeamLiveStatus> {
  return apiFetch<TeamLiveStatus>("/api/live/status", {
    scope: false,
    query: { team_slug: slug },
  });
}

/** `POST /api/teams/{id}/stream-key/rotate` — generate + persist a new
 * stream key for one team. Returns the new key + the publish path. */
export function rotateTeamStreamKey(
  teamId: string,
): Promise<TeamStreamKeyResponse> {
  return apiFetch<TeamStreamKeyResponse>(
    `/api/teams/${encodeURIComponent(teamId)}/stream-key/rotate`,
    { method: "POST", scope: false },
  );
}

/** `GET /api/teams/{id}/stream-key/audit` — who rotated last, when.
 * Same auth gate as the rotate endpoint; used to surface a "Last
 * rotated by X at T" notice so two operators racing on the same key
 * can see they're stepping on each other. */
export function getTeamStreamKeyAudit(
  teamId: string,
): Promise<TeamStreamKeyAudit> {
  return apiFetch<TeamStreamKeyAudit>(
    `/api/teams/${encodeURIComponent(teamId)}/stream-key/audit`,
    { scope: false },
  );
}

/** One entry in the per-team live-status aggregate response. */
export type TeamLiveStatusEntry = {
  slug: string;
  active: boolean;
  ready: boolean;
};

/** Admin variant — includes `team_id`, `name`, and `public`. */
export type AdminTeamLiveStatusEntry = TeamLiveStatusEntry & {
  team_id: string;
  name: string;
  public: boolean;
};

export type PublicTeamLiveStatusesResponse = {
  reachable: boolean;
  teams: TeamLiveStatusEntry[];
};

export type AdminTeamLiveStatusesResponse = {
  reachable: boolean;
  teams: AdminTeamLiveStatusEntry[];
};

/** `GET /api/live/team-statuses` — anonymous aggregate of public-team
 * live state. Never includes stream_key/team_id/name. One MediaMTX
 * call per request regardless of team count. */
export function getPublicTeamLiveStatuses(): Promise<PublicTeamLiveStatusesResponse> {
  return apiFetch<PublicTeamLiveStatusesResponse>("/api/live/team-statuses", {
    scope: false,
  });
}

/** `GET /api/admin/live/team-statuses` — admin-only aggregate of every
 * team's live state. Backs the `/admin/teams` pill column. */
export function getAdminTeamLiveStatuses(): Promise<AdminTeamLiveStatusesResponse> {
  return apiFetch<AdminTeamLiveStatusesResponse>(
    "/api/admin/live/team-statuses",
    { scope: false },
  );
}

export function createMatch(
  input: CreateMatchRequest,
  options?: { teamId?: string | null },
): Promise<Match> {
  return apiFetch<Match>("/api/matches", {
    method: "POST",
    body: input,
    scope: options?.teamId
      ? { teamId: options.teamId, seasonId: null }
      : undefined,
  });
}

export function updateMatch(
  matchId: string,
  input: UpdateMatchRequest,
  updatedAt?: string,
  options?: { teamId?: string | null },
): Promise<Match> {
  return apiFetch<Match>(`/api/matches/${encodeURIComponent(matchId)}`, {
    method: "PUT",
    body: input,
    headers: updatedAt ? { "If-Match": updatedAt } : undefined,
    scope: options?.teamId
      ? { teamId: options.teamId, seasonId: null }
      : undefined,
  });
}

/** HLS + MP4 stream URLs for a match slot.
 *
 * Pure URL builder — no fetch. The HLS path is team-aware when the
 * match carries a `team_id` (storage tiering routes team media
 * under `/teams/{team_id}/...`), and falls back to the legacy
 * flat path for older / team-less matches. The MP4 URL is the
 * direct-download fallback `<HlsVideo>` uses when HLS can't load.
 *
 * Takes the `Match` object rather than just an id so it doesn't
 * need a match-list lookup — the caller already has the row (from
 * the bundle or the matches list). These are same-origin paths;
 * `apiFetch`'s auth/credentials don't apply (the `<video>` element
 * fetches them itself, and the backend gates HLS via the streams
 * registry / heartbeat, not a Bearer header).
 *
 * Mirrors legacy `js/player.js:getStreamUrls`. */
export function getStreamUrls(
  match: Pick<Match, "id" | "team_id">,
  slot: string,
): { hlsUrl: string; mp4Url: string } {
  const hlsUrl = match.team_id
    ? `/api/matches/${match.id}/hls/teams/${match.team_id}/${slot}/master.m3u8`
    : `/api/matches/${match.id}/hls/${slot}/master.m3u8`;
  return {
    hlsUrl,
    mp4Url: `/api/matches/${match.id}/video/${slot}`,
  };
}

/** Anonymous public match playback URLs.
 *
 * The public `/match/$slug` surface mirrors legacy `js/player.js`: the
 * browser receives the unscoped HLS manifest URL and the backend resolves the
 * match's current storage path. That avoids baking a possibly stale team id
 * into long-lived Safari media requests while scoped coach/review surfaces can
 * keep using `getStreamUrls(match, slot)`.
 */
export function getPublicStreamUrls(
  match: Pick<Match, "id">,
  slot: string,
): { hlsUrl: string; mp4Url: string } {
  return {
    hlsUrl: `/api/matches/${match.id}/hls/${slot}/master.m3u8`,
    mp4Url: `/api/matches/${match.id}/video/${slot}`,
  };
}

export function publicAirPlayVariantUrlFromMaster(
  masterText: string,
  masterUrl: string,
): string | null {
  const lines = masterText.split(/\r?\n/);
  let pendingBandwidth: number | null = null;
  let best: { bandwidth: number; url: string } | null = null;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    if (line.startsWith("#EXT-X-STREAM-INF")) {
      const match = line.match(/(?:^|[:,])BANDWIDTH=(\d+)(?:,|$)/i);
      const bandwidth = match
        ? Number.parseInt(match[1], 10)
        : Number.MAX_SAFE_INTEGER;
      pendingBandwidth = Number.isFinite(bandwidth)
        ? bandwidth
        : Number.MAX_SAFE_INTEGER;
      continue;
    }
    if (line.startsWith("#") || pendingBandwidth == null) continue;

    const url = new URL(line, new URL(masterUrl, window.location.href)).href;
    if (!best || pendingBandwidth < best.bandwidth) {
      best = { bandwidth: pendingBandwidth, url };
    }
    pendingBandwidth = null;
  }

  return best?.url ?? null;
}

export async function resolvePublicAirPlayHlsUrl(
  masterUrl: string,
  signal?: AbortSignal,
): Promise<string | null> {
  // Low-level media exception: this fetch reads the public HLS master as text
  // so AirPlay can hand WebKit a real variant playlist instead of guessing a
  // preset directory name. JSON apiFetch is intentionally not involved here.
  const response = await fetch(masterUrl, {
    credentials: "same-origin",
    signal,
  });
  if (!response.ok) return null;
  return publicAirPlayVariantUrlFromMaster(await response.text(), response.url);
}

// Coaching reads (scoped). Bundle responses defer to later milestones;
// here we type the individual GET-list responses the M2 plumbing needs.

/**
 * Coaching notes for the active scope. `matchId` narrows to one
 * match's notes — the Coach Review timeline chip rail uses this to
 * fetch just the selected match's notes. The backend
 * (`coach_notes.coach_list_notes`) takes `?match_id=` and still
 * applies the team-scope check. The `match_id` filter rides in the
 * `query` option (NOT embedded in the path) so `buildUrl` merges it
 * with the auto-injected scope params under one `?`. */
export function getCoachingNotes(
  matchId?: string,
): Promise<{ notes: CoachingNote[] }> {
  return apiFetch<{ notes: CoachingNote[] }>("/api/coach/notes", {
    query: matchId ? { match_id: matchId } : undefined,
  });
}

/**
 * Coaching clips for the active scope. `matchId` narrows to one
 * match's clips — Coach Review's clip rail / clip-track lane use
 * this to fetch just the selected match's clips. The backend
 * (`coach_clips.coach_list_clips`) takes `?match_id=` and still
 * applies the team-scope check. The `match_id` filter rides in the
 * `query` option (NOT embedded in the path) so `buildUrl` merges it
 * with the auto-injected scope params under one `?`. */
export function getCoachingClips(
  matchId?: string,
): Promise<{ clips: CoachingClip[] }> {
  return apiFetch<{ clips: CoachingClip[] }>("/api/coach/clips", {
    query: matchId ? { match_id: matchId } : undefined,
  });
}

export function getCoachingPlaylists(): Promise<{
  playlists: CoachingPlaylist[];
}> {
  return apiFetch<{ playlists: CoachingPlaylist[] }>("/api/coach/playlists");
}

/** Create a review playlist. `note_ids` are the ordered items;
 * `player_ids` tag the playlist for those players. */
export function createCoachingPlaylist(
  body: CreateCoachingPlaylistRequest,
): Promise<{ playlist: CoachingPlaylist }> {
  return apiFetch<{ playlist: CoachingPlaylist }>("/api/coach/playlists", {
    method: "POST",
    body,
  });
}

/** Patch a review playlist. */
export function updateCoachingPlaylist(
  id: number,
  body: UpdateCoachingPlaylistRequest,
): Promise<{ playlist: CoachingPlaylist }> {
  return apiFetch<{ playlist: CoachingPlaylist }>(
    `/api/coach/playlists/${id}`,
    { method: "PATCH", body },
  );
}

/** Delete a review playlist. */
export function deleteCoachingPlaylist(id: number): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/coach/playlists/${id}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Match summaries (M6a) — match-level coach recaps with linked
// note/clip/playlist evidence.
// ---------------------------------------------------------------------------

/** The backend route keys the list response as `summaries`
 * (NOT `match_summaries`) — `routers/coach_summaries.py`. */
export function getMatchSummaries(): Promise<{
  summaries: MatchSummary[];
}> {
  return apiFetch<{ summaries: MatchSummary[] }>("/api/coach/match-summaries");
}

/** Create returns the row keyed as `summary` (singular). */
export function createMatchSummary(
  body: CreateMatchSummaryRequest,
): Promise<{ summary: MatchSummary }> {
  return apiFetch<{ summary: MatchSummary }>("/api/coach/match-summaries", {
    method: "POST",
    body,
  });
}

export function updateMatchSummary(
  id: number,
  body: UpdateMatchSummaryRequest,
): Promise<{ summary: MatchSummary }> {
  return apiFetch<{ summary: MatchSummary }>(
    `/api/coach/match-summaries/${id}`,
    { method: "PATCH", body },
  );
}

export function deleteMatchSummary(id: number): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/coach/match-summaries/${id}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Coach engagement dashboard (M6a). The route wraps the payload
// under `{ engagement: {...} }`; this verb unwraps it.
// ---------------------------------------------------------------------------

export function getCoachEngagement(
  filters: CoachEngagementFilters = {},
): Promise<CoachEngagement> {
  // Drop empty / undefined filter values so they're sent as
  // OMITTED params, not blank strings the backend would reject.
  const query: Record<string, string | number> = {};
  if (filters.player_id) query.player_id = filters.player_id;
  if (filters.playlist_id != null) query.playlist_id = filters.playlist_id;
  if (filters.match_id) query.match_id = filters.match_id;
  if (filters.visibility) query.visibility = filters.visibility;
  if (filters.start_date) query.start_date = filters.start_date;
  if (filters.end_date) query.end_date = filters.end_date;
  return apiFetch<{ engagement: CoachEngagement }>("/api/coach/engagement", {
    query,
  }).then((r) => r.engagement);
}

// ---------------------------------------------------------------------------
// Player goals (M6a) — first-class action items. `playerId` narrows
// the list to one player (the Roster surface uses this).
// ---------------------------------------------------------------------------

export function getPlayerGoals(
  playerId?: string,
): Promise<{ goals: PlayerGoal[] }> {
  return apiFetch<{ goals: PlayerGoal[] }>("/api/coach/goals", {
    query: playerId ? { player_id: playerId } : undefined,
  });
}

export function createPlayerGoal(
  body: CreatePlayerGoalRequest,
  scope?: CoachingTransportScope,
): Promise<{ goal: PlayerGoal }> {
  return apiFetch<{ goal: PlayerGoal }>("/api/coach/goals", {
    method: "POST",
    body,
    scope,
  });
}

/** `UpdatePlayerGoalRequest` is `extra="forbid"` + omits
 * `player_id` (a goal's player is immutable) — send only the
 * changed fields. */
export function updatePlayerGoal(
  id: number,
  body: UpdatePlayerGoalRequest,
): Promise<{ goal: PlayerGoal }> {
  return apiFetch<{ goal: PlayerGoal }>(`/api/coach/goals/${id}`, {
    method: "PATCH",
    body,
  });
}

export function deletePlayerGoal(id: number): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/coach/goals/${id}`, {
    method: "DELETE",
  });
}

/** Coach ack for the player responses behind one board card.
 *
 * Takes explicit `(user_id, item)` pairs rather than an item id: a card
 * groups one player's responses, and a player maps to several accounts
 * (themselves plus guardians), so acking by item alone would clear every
 * responder on a team-visible note. */
export function ackCoachingReviews(
  targets: ReadonlyArray<{
    user_id: string;
    item_kind: "note" | "clip";
    item_id: number;
  }>,
): Promise<{ ok: true; acked: number }> {
  return apiFetch<{ ok: true; acked: number }>("/api/coach/reviews/ack", {
    method: "POST",
    body: { targets },
  });
}

/** Coach ack for a goal's player reflections — clears
 * `needs_coach_follow_up` so the goal stops asking for attention
 * without archiving it away from the player. */
export function ackPlayerGoalReflections(
  id: number,
): Promise<{ ok: true; cleared: number; goal: PlayerGoal }> {
  return apiFetch<{ ok: true; cleared: number; goal: PlayerGoal }>(
    `/api/coach/goals/${id}/ack-reflections`,
    { method: "POST" },
  );
}

// ---------------------------------------------------------------------------
// Player feedback (M7) — the viewer-facing `/feedback` surfaces.
// Everything here is ALREADY viewer-filtered + `coach_private_note`-
// scrubbed server-side. Scope is auto-injected by `apiFetch`: the
// backend resolves the viewer's team from the scope store
// (single-team viewers "just work"; `useMe()` reconciles it on load).
// ---------------------------------------------------------------------------

/** The one-round-trip bundle feeding every `/feedback` list tab. */
export function getMyFeedback(): Promise<FeedbackBundle> {
  return apiFetch<FeedbackBundle>("/api/my-feedback");
}

export function requestPlayerPhoto(
  playerId: string,
  change: { file: File; avatarStyle?: never } | { file?: never; avatarStyle: "neutral" | "female" | "male" },
): Promise<{ ok: true; request: PlayerPhotoRequest }> {
  const form = new FormData();
  if (change.file) form.set("file", change.file);
  if (change.avatarStyle) form.set("avatar_style", change.avatarStyle);
  return apiFormFetch<{ ok: true; request: PlayerPhotoRequest }>(
    `/api/my-feedback/players/${encodeURIComponent(playerId)}/photo-request`,
    form,
  );
}

export function getNotifications(): Promise<NotificationsPayload> {
  return apiFetch<NotificationsPayload>("/api/notifications");
}

export function markNotificationsRead(body: {
  keys?: string[];
  all_current?: boolean;
}): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>("/api/notifications/read", {
    method: "POST",
    body,
  });
}

export function dismissNotification(key: string): Promise<{ ok: true }> {
  return dismissNotifications({ key });
}

export function dismissNotifications(body: {
  key?: string;
  keys?: string[];
  all_current?: boolean;
}): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>("/api/notifications/dismiss", {
    method: "POST",
    body,
  });
}

/** The viewer-scoped development profile for a linked player.
 * Returns `null` on 404 — the backend collapses unknown players AND
 * unrelated viewers to the same 404 so a viewer can't probe whether
 * a roster id exists; the caller treats `null` as "not available". */
export async function getMyPlayerDevelopment(
  playerId: string,
): Promise<DevelopmentProfile | null> {
  try {
    const res = await apiFetch<{ profile: DevelopmentProfile }>(
      `/api/my-feedback/players/${encodeURIComponent(playerId)}/development`,
    );
    return res.profile;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** The coach-side development profile — includes the coach-only
 * `themes` + `linked_accounts`. Throws on any non-200 (the coach
 * surface wants the error surfaced, not swallowed). */
export async function getCoachPlayerDevelopment(
  playerId: string,
): Promise<DevelopmentProfile> {
  const res = await apiFetch<{ profile: DevelopmentProfile }>(
    `/api/coach/players/${encodeURIComponent(playerId)}/development`,
  );
  return res.profile;
}

/** Mark a note, playlist, or clip done. Exactly one of the three
 * ids must be set per call — enforced by the router with a 422.
 * The backend re-validates visibility at mark-time, so a guessed
 * id 403s rather than recording a review.
 *
 * PR 7 of the Feedback Player UX redesign added `clip_id` as a
 * third target. Older callers that only pass `note_id`/`playlist_id`
 * keep working unchanged. */
export function markFeedbackReviewed(body: {
  note_id?: number;
  playlist_id?: number;
  clip_id?: number;
  /** Phase B: mark a match recap (summary) read. */
  summary_id?: number;
  reflection?: string;
  /** Phase B: the response signal as a dedicated field (no
   * `[SIGNAL:*]` prefix). `""` clears; omit to preserve. */
  response_signal?: "got_it" | "need_help" | "tried_this" | "";
  /** Stable retry key for this exact response operation. */
  client_operation_id?: string;
}): Promise<{ ok: true; review: FeedbackReview }> {
  return apiFetch<{ ok: true; review: FeedbackReview }>(
    "/api/my-feedback/review",
    { method: "POST", body },
  );
}

/** Add a viewer reflection to one of the viewer's visible goals. */
export function createMyGoalReflection(
  goalId: number,
  reflection: string,
): Promise<{ ok: true; reflection: PlayerGoalReflection }> {
  const body: CreatePlayerGoalReflectionRequest = { reflection };
  return apiFetch<{ ok: true; reflection: PlayerGoalReflection }>(
    `/api/my-feedback/goals/${goalId}/reflection`,
    { method: "POST", body },
  );
}

// ---------------------------------------------------------------------------
// Roster (M6a) — players + account links. The list endpoint returns
// inactive players too (the roster page filters client-side); scope
// is auto-injected.
// ---------------------------------------------------------------------------

/** The active team's roster rows (assignments). Ended assignments —
 * removed or transferred out (#908 §5.4) — are hidden unless
 * `includeEnded` is set; each row carries `assignment_status` + dates. */
export function getRoster(options?: {
  includeEnded?: boolean;
}): Promise<{ players: Player[] }> {
  return apiFetch<{ players: Player[] }>("/api/coach/players", {
    query: { include_ended: options?.includeEnded ? true : undefined },
  });
}

/** Linkable team users for the roster link modal's account picker.
 * Server strips `password_hash`. */
export function getCoachUsers(): Promise<{ users: CoachUser[] }> {
  return apiFetch<{ users: CoachUser[] }>("/api/coach/users");
}

/** Create a roster row on the active team. Since #908 the body may name an
 * existing `athlete_id` to roster (directory search, D14) instead of
 * creating a new athlete from `display_name`, plus the assignment's
 * `starts_on`. */
export function createPlayer(
  body: CreatePlayerRequest,
): Promise<{ player: Player }> {
  return apiFetch<{ player: Player }>("/api/coach/players", {
    method: "POST",
    body,
  });
}

/** `UpdatePlayerRequest` is `extra="forbid"` — send only the fields
 * being changed. */
export function updatePlayer(
  id: string,
  body: UpdatePlayerRequest,
): Promise<{ player: Player }> {
  return apiFetch<{ player: Player }>(
    `/api/coach/players/${encodeURIComponent(id)}`,
    { method: "PATCH", body },
  );
}

export function deletePlayer(id: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(
    `/api/coach/players/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}

export function uploadCoachPlayerPhoto(
  id: string,
  file: File,
): Promise<{ ok: true; player: Player }> {
  const form = new FormData();
  form.set("file", file);
  return apiFormFetch<{ ok: true; player: Player }>(
    `/api/coach/players/${encodeURIComponent(id)}/photo`,
    form,
  );
}

export function removeCoachPlayerPhoto(
  id: string,
): Promise<{ ok: true; player: Player }> {
  return apiFetch<{ ok: true; player: Player }>(
    `/api/coach/players/${encodeURIComponent(id)}/photo`,
    { method: "DELETE" },
  );
}

export function getPlayerPhotoRequests(
  status: "pending" | "approved" | "rejected" | "all" = "pending",
): Promise<{ requests: PlayerPhotoRequest[] }> {
  return apiFetch<{ requests: PlayerPhotoRequest[] }>(
    "/api/coach/player-photo-requests",
    { query: { status } },
  );
}

export function approvePlayerPhotoRequest(
  id: string,
): Promise<{ ok: true; player: Player; request: PlayerPhotoRequest }> {
  return apiFetch<{ ok: true; player: Player; request: PlayerPhotoRequest }>(
    `/api/coach/player-photo-requests/${encodeURIComponent(id)}/approve`,
    { method: "POST" },
  );
}

export function rejectPlayerPhotoRequest(
  id: string,
): Promise<{ ok: true; request: PlayerPhotoRequest }> {
  return apiFetch<{ ok: true; request: PlayerPhotoRequest }>(
    `/api/coach/player-photo-requests/${encodeURIComponent(id)}/reject`,
    { method: "POST" },
  );
}

export function createPlayerLink(
  body: CreatePlayerUserLinkRequest,
): Promise<{ ok: true; player: Player }> {
  return apiFetch<{ ok: true; player: Player }>("/api/coach/player-links", {
    method: "POST",
    body,
  });
}

export function deletePlayerLink(linkId: number): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/coach/player-links/${linkId}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// CSV roster import (M6b) — team-admin-only. `preview` never writes
// rows; `commit` is transactional (any error rolls the whole import
// back). Scope (`team_id` / `season_id`) is auto-injected.
// ---------------------------------------------------------------------------

/** Dry-run a CSV roster import. Returns the per-row plan + summary;
 * writes nothing. The backend gates this on the `team_admin` role —
 * a plain coach gets a 403. */
export function previewRosterImport(
  csvText: string,
): Promise<RosterImportPreviewResult> {
  return apiFetch<RosterImportPreviewResult>(
    "/api/coach/players/import/preview",
    { method: "POST", body: { csv_text: csvText } },
  );
}

/** Commit a CSV roster import. Transactional — a wrong-team
 * `player_id` (409) or any row error rolls back the entire import.
 * Returns the committed rows + any guardian invites created.
 *
 * #908 D16: pass `{ csv_text, decisions }` to carry the importer's reviewed
 * per-row choices (`athlete_id` = roster that existing athlete, `null` =
 * create a new one); a bare CSV string commits with the preview defaults
 * (rows with exactly one directory match use it). */
export function commitRosterImport(
  input: string | RosterImportCommitRequest,
): Promise<RosterImportCommitResult> {
  const body: RosterImportCommitRequest =
    typeof input === "string" ? { csv_text: input } : input;
  return apiFetch<RosterImportCommitResult>(
    "/api/coach/players/import/commit",
    { method: "POST", body },
  );
}

// ---------------------------------------------------------------------------
// #908 — Club athlete directory + roster assignments
// (docs/plans/athlete-directory-season-teams-plan.md). The directory is
// scoped to the HOST club, never a query param, so scope injection is
// suppressed; the caller needs club admin or `roster:manage` on a team of
// the club (403 otherwise; 404 on the apex host). Assignment routes take
// the team from the body / the player row and require `roster:manage` on
// it — a team outside the host club is 404. The directory discloses name,
// status and team/season assignments only (§5.3); `birth_year` is `null`
// unless the viewer is a club admin or the athlete's own team staff.
// ---------------------------------------------------------------------------

/** `GET /api/athletes` — directory search (D14). `query` matches the
 * display name; `status` narrows to one status (merged athletes are listed
 * only when `merged` is asked for explicitly). */
export function getAthletes(
  params: { query?: string; status?: AthleteDirectoryStatusFilter } = {},
): Promise<AthleteDirectoryEntry[]> {
  return apiFetch<AthleteDirectoryEntry[]>("/api/athletes", {
    scope: false,
    query: {
      query: params.query?.trim() || undefined,
      status: params.status || undefined,
    },
  });
}

/** `POST /api/athletes` — create a club athlete with no assignment yet. */
export function createAthlete(body: AthleteCreate): Promise<Athlete> {
  return apiFetch<Athlete>("/api/athletes", {
    method: "POST",
    body,
    scope: false,
  });
}

/** `GET /api/athletes/{id}` — athlete + every assignment; `links` only for
 * club admins and current team staff (`can_manage_links`), `merges` for
 * club admins (`can_merge`). */
export function getAthlete(athleteId: string): Promise<AthleteDetail> {
  return apiFetch<AthleteDetail>(
    `/api/athletes/${encodeURIComponent(athleteId)}`,
    { scope: false },
  );
}

/** `PATCH /api/athletes/{id}` — edit names / birth year (club admins and
 * the athlete's current team staff); `status` is club-admin only. */
export function updateAthlete(
  athleteId: string,
  body: AthleteUpdate,
): Promise<Athlete> {
  return apiFetch<Athlete>(`/api/athletes/${encodeURIComponent(athleteId)}`, {
    method: "PATCH",
    body,
    scope: false,
  });
}

/** `DELETE /api/athletes/{id}` — permanently delete an athlete (204; club
 * admin only). 409 unless the athlete is archived and has no associated
 * data; the structured 409 detail (`code: "athlete_has_data"`) lists the
 * blocking kinds with counts, as `AthleteDetail.deletion` does. */
export function deleteAthlete(athleteId: string): Promise<void> {
  return apiFetch<void>(`/api/athletes/${encodeURIComponent(athleteId)}`, {
    method: "DELETE",
    scope: false,
  });
}

/** `POST /api/athletes/{id}/links` — link an account to the athlete (self /
 * guardian / family, D4). The service projects the link onto every
 * assignment the athlete holds (`player_user_links` + memberships). */
export function createAthleteLink(
  athleteId: string,
  body: AthleteLinkCreate,
): Promise<AthleteUserLink> {
  return apiFetch<AthleteUserLink>(
    `/api/athletes/${encodeURIComponent(athleteId)}/links`,
    { method: "POST", body, scope: false },
  );
}

/** `DELETE /api/athletes/{id}/links/{link_id}` — revoke an account link
 * (204). Drops the projected links + memberships on every assignment. */
export function deleteAthleteLink(
  athleteId: string,
  linkId: number,
): Promise<void> {
  return apiFetch<void>(
    `/api/athletes/${encodeURIComponent(athleteId)}/links/${encodeURIComponent(String(linkId))}`,
    { method: "DELETE", scope: false },
  );
}

/** `POST /api/athletes/{survivor_id}/merge` — reviewed merge (D10, club
 * admin only): the merged athlete's assignments are repointed onto the
 * survivor and an audit row is written. 409 when both athletes hold an
 * assignment on the same team. */
export function mergeAthletes(
  survivorAthleteId: string,
  body: AthleteMergeRequest,
): Promise<AthleteMerge> {
  return apiFetch<AthleteMerge>(
    `/api/athletes/${encodeURIComponent(survivorAthleteId)}/merge`,
    { method: "POST", body, scope: false },
  );
}

/** `POST /api/roster/assignments` — roster an existing athlete
 * (`athlete_id`) or create one inline (`athlete`) on `team_id`: exactly one
 * of the two, one assignment. Returns the new roster row (`Player`). */
export function createRosterAssignment(
  body: RosterAssignmentCreate,
): Promise<Player> {
  return apiFetch<Player>("/api/roster/assignments", {
    method: "POST",
    body,
    scope: false,
  });
}

/** `POST /api/roster/assignments/bulk` — the carry-forward / bulk roster
 * screen: several existing athletes onto one team, all-or-nothing. A row
 * error surfaces as a 4xx whose detail is prefixed `Item N: …`. */
export function createRosterAssignmentsBulk(
  body: RosterAssignmentBulkCreate,
): Promise<Player[]> {
  return apiFetch<Player[]>("/api/roster/assignments/bulk", {
    method: "POST",
    body,
    scope: false,
  });
}

/** `POST /api/roster/assignments/{player_id}/end` — end an assignment
 * (remove from team / transfer out, §5.4). The confirmation copy must say
 * this removes the athlete's access to this season's team content (D12). */
export function endRosterAssignment(
  playerId: string,
  body: RosterAssignmentEnd = {},
): Promise<Player> {
  return apiFetch<Player>(
    `/api/roster/assignments/${encodeURIComponent(playerId)}/end`,
    { method: "POST", body, scope: false },
  );
}

/** `POST /api/roster/assignments/{player_id}/transfer` — end this
 * assignment and create the same athlete's assignment on `to_team_id` in
 * one transaction (§6 step 5). Needs `roster:manage` on both teams. */
export function transferRosterAssignment(
  playerId: string,
  body: RosterAssignmentTransfer,
): Promise<RosterAssignmentTransferResult> {
  return apiFetch<RosterAssignmentTransferResult>(
    `/api/roster/assignments/${encodeURIComponent(playerId)}/transfer`,
    { method: "POST", body, scope: false },
  );
}

// ---------------------------------------------------------------------------
// Coach > Settings (M6b) — team-scoped AI governance + coaching
// default-visibility settings. GET is readable by coaches + team
// admins; PATCH is gated on the `team_settings:manage` capability
// (the response's `can_edit` mirrors that). Scope auto-injected.
// ---------------------------------------------------------------------------

/** Fetch the active team's settings + the closed schema registry the
 * Settings page renders its controls from. `can_edit` is `false` for
 * plain coaches (read-only view). */
export function getTeamSettings(): Promise<TeamSettingsResponse> {
  return apiFetch<TeamSettingsResponse>("/api/coach/team/settings");
}

/** Save team settings. Send only the keys being changed. A bad value
 * returns `422` with `{ code: "team_settings_validation_failed",
 * errors: [...] }` — none are applied if any fail. Returns the same
 * shape as GET, reflecting the saved state. */
export function patchTeamSettings(
  settings: TeamSettings,
): Promise<TeamSettingsResponse> {
  return apiFetch<TeamSettingsResponse>("/api/coach/team/settings", {
    method: "PATCH",
    body: { settings },
  });
}

// ---------------------------------------------------------------------------
// Coach Review (M4 surface)
// ---------------------------------------------------------------------------

/** Fetch the Coach Review bundle — single round-trip aggregator for
 * the Coach Review shell mount. See `CoachReviewBundle` in
 * `./types.ts` for the response shape. Scope is auto-injected.
 *
 * Edit modes: `noteId` seeds the composer with an existing note,
 * `clipId` seeds with a clip. They're mutually exclusive — server
 * 400s if both are passed. The bundle auto-resolves the associated
 * match for either edit mode, so the client doesn't have to pass
 * `matchId` explicitly.
 */
export function getCoachReviewBundle(
  input: {
    matchId?: string;
    noteId?: number;
    clipId?: number;
  } = {},
): Promise<CoachReviewBundle> {
  return apiFetch<CoachReviewBundle>("/api/coach/review/bundle", {
    query: {
      match_id: input.matchId,
      note_id: input.noteId,
      clip_id: input.clipId,
    },
  });
}

/** Create a coaching note. Returns the created note (server echoes
 * the full row). The form's structured-feedback fields are part of
 * `CreateCoachingNoteRequest` — see `models.py` for the canonical
 * shape; the generated request type is what the editor's React Hook
 * Form schema mirrors.
 *
 * The save flow carries BOTH the tactical-board scene (under
 * `tactical_board_json`) AND the telestrator drawing (under
 * `drawing`) — they're separate columns on `coaching_notes` and
 * either can be populated independently.
 */
/** Resolve a coach-pasted video URL into a stored-shape reference (#675).
 *
 * The server parses the URL, fetches title + poster from the provider's
 * oEmbed endpoint, and returns the derived `(provider, video_id)` pair. The
 * raw URL is discarded — the composer stores what comes back here, never
 * what the coach typed, so the value that eventually reaches an `iframe
 * src` is one the server constructed.
 *
 * A 400 carries a coach-readable message (unsupported provider, malformed
 * link, private or deleted video) that the field surfaces directly. */
export function resolveReferenceVideo(url: string): Promise<{
  ok: true;
  reference_video: {
    provider: "youtube" | "vimeo" | "file";
    video_id: string;
    title: string;
    thumb: string | null;
    thumb_url: string | null;
  };
}> {
  return apiFetch("/api/coach/reference-video/resolve", {
    method: "POST",
    body: { url },
  });
}

export function createCoachingNote(
  body: CreateCoachingNoteRequest,
  scope?: CoachingTransportScope,
): Promise<{ note: CoachingNote }> {
  return apiFetch<{ note: CoachingNote }>("/api/coach/notes", {
    method: "POST",
    body,
    scope,
  });
}

/** Patch an existing coaching note. The backend's request model has
 * `extra="forbid"`, so passing fields outside `UpdateCoachingNote-
 * Request` will 422. */
export function updateCoachingNote(
  id: number,
  body: UpdateCoachingNoteRequest,
  scope?: { teamId: string | null; seasonId: string | null },
): Promise<{ note: CoachingNote }> {
  return apiFetch<{ note: CoachingNote }>(`/api/coach/notes/${id}`, {
    method: "PATCH",
    body,
    scope,
  });
}

/** Delete a coaching note (note OR observation). */
export function deleteCoachingNote(id: number): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/coach/notes/${id}`, {
    method: "DELETE",
  });
}

/** Regenerate a video note's thumbnail JPEG. Best-effort
 * server-side — `{ ok: true, generated: bool }`; `generated`
 * is false when the source video is missing. */
export function regenerateCoachingNoteThumbnail(
  id: number,
): Promise<{ ok: true; generated: boolean }> {
  return apiFetch<{ ok: true; generated: boolean }>(
    `/api/coach/notes/${id}/thumbnail/regenerate`,
    { method: "POST" },
  );
}

/** Create a coaching clip. */
export function createCoachingClip(
  body: CreateCoachingClipRequest,
  scope?: { teamId: string | null; seasonId: string | null },
): Promise<{ clip: CoachingClip }> {
  return apiFetch<{ clip: CoachingClip }>("/api/coach/clips", {
    method: "POST",
    body,
    scope,
  });
}

/** Patch an existing coaching clip. */
export function updateCoachingClip(
  id: number,
  body: UpdateCoachingClipRequest,
  scope?: { teamId: string | null; seasonId: string | null },
): Promise<{ clip: CoachingClip }> {
  return apiFetch<{ clip: CoachingClip }>(`/api/coach/clips/${id}`, {
    method: "PATCH",
    body,
    scope,
  });
}

/** Delete a coaching clip. */
export function deleteCoachingClip(id: number): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/coach/clips/${id}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// M8a — Admin verbs
// ---------------------------------------------------------------------------
// All admin reads + writes are GLOBAL — scope-injection is suppressed via
// `scope: false`. The backend gates by global-admin (or team-admin where
// noted in the inventory's auth column); the client does NOT carry an
// active team/season into these calls.

/** `GET /api/admin/settings` — full settings + tuning knobs. */
export function getAdminSettings(): Promise<AdminSettingsPayload> {
  return apiFetch<AdminSettingsPayload>("/api/admin/settings", {
    scope: false,
  });
}

/** `PUT /api/admin/settings` — partial update of any combination of
 * settings + tuning knobs. The backend validates against
 * `EDITABLE_APP_SETTING_KEYS`. Pass only the keys that changed. */
export function updateAdminSettings(
  body: Record<string, unknown>,
): Promise<AdminSettingsPayload & { ok: true }> {
  return apiFetch<AdminSettingsPayload & { ok: true }>("/api/admin/settings", {
    method: "PUT",
    body,
    scope: false,
  });
}

// --- Multi-club: super-admin club management + per-club settings ---

/** `GET /api/super-admin/clubs` — list every club (super-admin only). */
/** Default (no filter) hides archived clubs; pass a status or "all". */
export function listClubs(
  status?: ClubStatus | "all",
): Promise<AdminClubRow[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return apiFetch<AdminClubRow[]>(`/api/super-admin/clubs${qs}`, {
    scope: false,
  });
}

/** `DELETE /api/super-admin/clubs/{id}` — hard-delete an ARCHIVED club
 * (full DB cascade + background media cleanup). 409 `club_not_archived`
 * unless archived; 422 `confirm_name_mismatch` unless the exact name. */
export function deleteClub(
  clubId: string,
  confirmName: string,
): Promise<{
  ok: boolean;
  deleted_club_id: string;
  teams: number;
  matches: number;
}> {
  return apiFetch(`/api/super-admin/clubs/${encodeURIComponent(clubId)}`, {
    method: "DELETE",
    body: { confirm_name: confirmName },
    scope: false,
  });
}

/** `POST /api/super-admin/clubs` — provision a club + subdomain. */
export function createClub(body: CreateClubRequest): Promise<Club> {
  return apiFetch<Club>("/api/super-admin/clubs", {
    method: "POST",
    body,
    scope: false,
  });
}

/** `PATCH /api/super-admin/clubs/{id}/status` — activate / suspend. */
export function setClubStatus(
  clubId: string,
  status: ClubStatus,
): Promise<Club> {
  return apiFetch<Club>(
    `/api/super-admin/clubs/${encodeURIComponent(clubId)}/status`,
    {
      method: "PATCH",
      body: { status },
      scope: false,
    },
  );
}

/** `POST /api/super-admin/clubs/{id}/admins` — grant a user club_admin. */
export function grantClubAdmin(
  clubId: string,
  userId: string,
): Promise<{ club_id: string; user_id: string; role: string }> {
  return apiFetch(
    `/api/super-admin/clubs/${encodeURIComponent(clubId)}/admins`,
    {
      method: "POST",
      body: { user_id: userId },
      scope: false,
    },
  );
}

/** `GET /api/super-admin/clubs/{id}` — composite for the club detail page:
 * identity, rollup counts, operator-controllable flags (+ overridden keys),
 * and the operator-only metadata. */
export function getSuperAdminClubDetail(
  clubId: string,
): Promise<AdminClubDetail> {
  return apiFetch(`/api/super-admin/clubs/${encodeURIComponent(clubId)}`, {
    scope: false,
  });
}

/** `PUT /api/super-admin/clubs/{id}/settings` — operator edit of the club's
 * feature/onboarding flags (CLUB_FLAG_KEYS only; branding is rejected). */
export function updateSuperAdminClubFlags(
  clubId: string,
  body: Partial<Record<ClubFlagKey, boolean>>,
): Promise<{ flags: Record<ClubFlagKey, string> }> {
  return apiFetch(
    `/api/super-admin/clubs/${encodeURIComponent(clubId)}/settings`,
    {
      method: "PUT",
      body,
      scope: false,
    },
  );
}

/** `PUT /api/super-admin/clubs/{id}/meta` — operator-only bookkeeping
 * (contact / plan label / internal notes). Partial: only provided keys
 * change; empty string clears a field. */
export function updateSuperAdminClubMeta(
  clubId: string,
  body: Partial<
    Pick<
      ClubOperatorMeta,
      "contact_name" | "contact_email" | "plan_label" | "operator_notes"
    >
  >,
): Promise<{ meta: ClubOperatorMeta }> {
  return apiFetch(`/api/super-admin/clubs/${encodeURIComponent(clubId)}/meta`, {
    method: "PUT",
    body,
    scope: false,
  });
}

/** `GET /api/super-admin/clubs/{id}/usage` — usage vs quota (lazy; the
 * storage walk is 60s-cached server-side). */
export function getSuperAdminClubUsage(clubId: string): Promise<ClubUsage> {
  return apiFetch(
    `/api/super-admin/clubs/${encodeURIComponent(clubId)}/usage`,
    {
      scope: false,
    },
  );
}

/** `PUT /api/super-admin/clubs/{id}/quotas` — set/clear operator quotas.
 * Only provided keys change; an explicit null clears back to unlimited. */
export function updateSuperAdminClubQuotas(
  clubId: string,
  body: {
    storage_quota_bytes?: number | null;
    max_teams?: number | null;
    max_members?: number | null;
  },
): Promise<AdminClubRow> {
  return apiFetch(
    `/api/super-admin/clubs/${encodeURIComponent(clubId)}/quotas`,
    {
      method: "PUT",
      body,
      scope: false,
    },
  );
}

/** `GET /api/super-admin/clubs/{id}/admins` — seated club admins (same
 * envelope as the club-side `GET /api/club/admins`). */
export function listSuperAdminClubAdmins(
  clubId: string,
): Promise<{ admins: ClubAdminEntry[] }> {
  return apiFetch(
    `/api/super-admin/clubs/${encodeURIComponent(clubId)}/admins`,
    {
      scope: false,
    },
  );
}

/** `GET /api/super-admin/clubs/{id}/admins/candidates` — club members who are
 * not already admins (the promote picker's source). */
export function listSuperAdminClubAdminCandidates(clubId: string): Promise<{
  candidates: Array<
    Pick<ClubAdminEntry, "user_id" | "username" | "display_name">
  >;
}> {
  return apiFetch(
    `/api/super-admin/clubs/${encodeURIComponent(clubId)}/admins/candidates`,
    { scope: false },
  );
}

/** `DELETE /api/super-admin/clubs/{id}/admins/{userId}` — demote a seated
 * club admin (they stay a plain member). 409 `last_club_admin` when it would
 * strand the club with zero admins. */
export function revokeSuperAdminClubAdmin(
  clubId: string,
  userId: string,
): Promise<{ club_id: string; user_id: string; revoked: string }> {
  return apiFetch(
    `/api/super-admin/clubs/${encodeURIComponent(clubId)}/admins/${encodeURIComponent(userId)}`,
    { method: "DELETE", scope: false },
  );
}

/** `PATCH /api/super-admin/clubs/{id}` — edit a club's identity (name / slug /
 * subdomain). Distinct from setClubStatus. A subdomain change moves the
 * host→club mapping — the caller should confirm before renaming. */
export function updateClub(
  clubId: string,
  body: { name?: string; slug?: string; subdomain?: string },
): Promise<Club> {
  return apiFetch<Club>(
    `/api/super-admin/clubs/${encodeURIComponent(clubId)}`,
    {
      method: "PATCH",
      body,
      scope: false,
    },
  );
}

/** `POST /api/super-admin/clubs/{id}/admins/invite` — email the first club
 * admin an invite (they accept to create their account). */
export function inviteClubAdmin(
  clubId: string,
  email: string,
): Promise<{
  club_id: string;
  email: string;
  role: string;
  invite_token?: string;
}> {
  return apiFetch(
    `/api/super-admin/clubs/${encodeURIComponent(clubId)}/admins/invite`,
    {
      method: "POST",
      body: { email },
      scope: false,
    },
  );
}

/** One club-admin invite row (super-admin list surface). */
export interface ClubAdminInvite {
  id: string;
  club_id: string;
  email: string;
  role: string;
  status: string;
  expires_at: number;
  created_at: number;
  invite_token?: string;
}

/** `GET /api/super-admin/clubs/{id}/admins/invites` — all invites for a club,
 * newest first. */
export function listClubAdminInvites(
  clubId: string,
): Promise<ClubAdminInvite[]> {
  return apiFetch(
    `/api/super-admin/clubs/${encodeURIComponent(clubId)}/admins/invites`,
    {
      scope: false,
    },
  );
}

/** `POST /api/super-admin/clubs/{id}/admins/invites/{inviteId}/revoke` */
export function revokeClubAdminInvite(
  clubId: string,
  inviteId: string,
): Promise<ClubAdminInvite> {
  return apiFetch(
    `/api/super-admin/clubs/${encodeURIComponent(clubId)}/admins/invites/${encodeURIComponent(inviteId)}/revoke`,
    { method: "POST", scope: false },
  );
}

/** `POST /api/super-admin/clubs/{id}/admins/invites/{inviteId}/resend` —
 * rotates the token and re-emails; the old link stops working. */
export function resendClubAdminInvite(
  clubId: string,
  inviteId: string,
): Promise<ClubAdminInvite> {
  return apiFetch(
    `/api/super-admin/clubs/${encodeURIComponent(clubId)}/admins/invites/${encodeURIComponent(inviteId)}/resend`,
    { method: "POST", scope: false },
  );
}

/** `GET /api/club-admin/invites/{token}` — public accept-page descriptor
 * (club display name + role + usability). The token is the auth. */
export function describeClubAdminInvite(
  token: string,
): Promise<{ club_name: string; role: string; status: string }> {
  return apiFetch(`/api/club-admin/invites/${encodeURIComponent(token)}`, {
    scope: false,
  });
}

/** `POST /api/club-admin/invites/accept` — accept a club-admin invite,
 * creating the account (username/password) or linking the signed-in user. */
export function acceptClubAdminInvite(body: {
  token: string;
  username?: string;
  password?: string;
  display_name?: string;
}): Promise<{
  ok: boolean;
  club_id: string;
  user?: { id: string; username: string };
  role: string;
}> {
  return apiFetch("/api/club-admin/invites/accept", {
    method: "POST",
    body,
    scope: false,
  });
}

/** The host club's settings payload (club-admin surface). */
export interface ClubSettingsPayload {
  club_id: string;
  settings: Record<string, string>;
  /** Keys the club has explicitly set (vs inherited global fallbacks).
   * Present on GET only — drives the first-run setup checklist. */
  own_keys?: string[];
  assets: {
    club_logo_url?: string | null;
    favicon_url?: string | null;
  };
}

/** `GET /api/club/settings` — the host club's branding + feature flags. */
export function getClubSettings(): Promise<ClubSettingsPayload> {
  return apiFetch("/api/club/settings", { scope: false });
}

/** `GET /api/club/overview` — the club console's Overview payload: club
 * KPIs, per-team rollup, this club's slot/upload/transcode signals. The
 * club-scoped sibling of the operator diagnostics — deliberately WITHOUT
 * platform telemetry (disk, GPU, backfill, tokens, error log). */
export function getClubOverview(): Promise<ClubOverviewPayload> {
  return apiFetch<ClubOverviewPayload>("/api/club/overview", { scope: false });
}

/** `GET /api/club/live/status` — own-club per-team live state + viewer
 * counts (only sessions attributed to this club's teams). */
export function getClubLiveStatus(): Promise<ClubLiveStatusPayload> {
  return apiFetch<ClubLiveStatusPayload>("/api/club/live/status", {
    scope: false,
  });
}

/** `GET /api/club/live/config` — the live settings a club may see:
 * `{enabled, rtmp_public_url, offline_message}`. NEVER a stream key. */
export function getClubLiveConfig(): Promise<ClubLiveConfigPayload> {
  return apiFetch<ClubLiveConfigPayload>("/api/club/live/config", {
    scope: false,
  });
}

/** `PUT /api/club/settings` — update the host club's branding/flags. */
export function updateClubSettings(
  body: UpdateClubSettingsRequest,
): Promise<ClubSettingsPayload> {
  return apiFetch("/api/club/settings", { method: "PUT", body, scope: false });
}

/** `GET /api/transcode-progress` — public-no-auth live transcode
 * progress. Polled on `/admin/matches` only when at least one slot
 * is in `transcoding` state. */
export function getTranscodeProgress(): Promise<TranscodeProgressMap> {
  return apiFetch<TranscodeProgressMap>("/api/transcode-progress", {
    scope: false,
  });
}

/** `POST /api/admin/matches/{match_id}/slots/{slot}/retry` — re-
 * transcode. `force=true` allows ready→transcoding (operator
 * override when an apparently-OK slot needs to be rebuilt). */
export function retryAdminMatchSlot(
  matchId: string,
  slot: string,
  force = false,
): Promise<{ ok: true; match_id: string; slot: string; video_status: string }> {
  return apiFetch(
    `/api/admin/matches/${encodeURIComponent(matchId)}/slots/${encodeURIComponent(slot)}/retry`,
    { method: "POST", scope: false, query: force ? { force: "true" } : {} },
  );
}

/** `POST /api/admin/matches/{match_id}/regenerate-thumbnail` —
 * pick a new frame from the slot's MP4. */
export function regenerateAdminThumbnail(
  matchId: string,
): Promise<{ ok: true; match_id: string; thumbnail_generated_at: string }> {
  return apiFetch(
    `/api/admin/matches/${encodeURIComponent(matchId)}/regenerate-thumbnail`,
    { method: "POST", scope: false },
  );
}

// ---------------------------------------------------------------------------
// Super-admin operator console (admin.tktic.com → /super-admin).
//
// `/api/super-admin/*` twins of the legacy operator routes — apex-host-only,
// gated by require_super_admin on the server. Same payload shapes as their
// legacy siblings (the diagnostics twin additionally labels failed-slot /
// active-job rows with the owning club). The legacy verbs above keep serving
// the club console until its cutover.

/** `GET /api/super-admin/diagnostics` — cross-club operator diagnostics
 * (failed slots + active jobs carry `club_id`/`club_name`). */
export function getSuperAdminDiagnostics(): Promise<AdminDiagnostics> {
  return apiFetch<AdminDiagnostics>("/api/super-admin/diagnostics", {
    scope: false,
  });
}

/** `GET /api/super-admin/performance` — encoder/host telemetry. */
export function getSuperAdminPerformance(): Promise<AdminPerformance> {
  return apiFetch<AdminPerformance>("/api/super-admin/performance", {
    scope: false,
  });
}

/** `POST /api/super-admin/performance/capture` — 1 Hz capture window. */
export function startSuperAdminPerformanceCapture(
  seconds: number,
): Promise<{ fast: boolean; remaining_seconds: number }> {
  return apiFetch<{ fast: boolean; remaining_seconds: number }>(
    "/api/super-admin/performance/capture",
    { method: "POST", body: { seconds }, scope: false },
  );
}

/** `GET /api/super-admin/jobs` — cross-club background-job strip. */
export function getSuperAdminJobs(): Promise<AdminJobsPayload> {
  return apiFetch<AdminJobsPayload>("/api/super-admin/jobs", { scope: false });
}

/** `POST /api/super-admin/jobs/cleanup` — delete terminal jobs. */
export function cleanupSuperAdminJobs(
  body: {
    status?: "succeeded" | "failed" | "cancelled";
    limit?: number;
  } = {},
): Promise<{ ok: true; deleted: number; by_status: Record<string, number> }> {
  return apiFetch<{
    ok: true;
    deleted: number;
    by_status: Record<string, number>;
  }>("/api/super-admin/jobs/cleanup", { method: "POST", body, scope: false });
}

/** `GET /api/super-admin/files` — platform media/asset inventory. */
export function getSuperAdminFiles(): Promise<AdminFileInventoryResponse> {
  return apiFetch<AdminFileInventoryResponse>("/api/super-admin/files", {
    scope: false,
  });
}

/** `POST /api/super-admin/backfill-hls` — rebuild HLS for every
 * ready-but-no-HLS slot, platform-wide. */
export function backfillSuperAdminHls(): Promise<{
  ok: true;
  backfilled: number;
  failed: number;
}> {
  return apiFetch<{ ok: true; backfilled: number; failed: number }>(
    "/api/super-admin/backfill-hls",
    { method: "POST", scope: false },
  );
}

/** `GET /api/super-admin/streams` — active sessions + kill blocks. */
export function getSuperAdminStreams(): Promise<AdminStreamsPayload> {
  return apiFetch<AdminStreamsPayload>("/api/super-admin/streams", {
    scope: false,
  });
}

/** `POST /api/super-admin/streams/{session_id}/kill` — terminate a viewer
 * session and short-list its (ip, target). */
export function killSuperAdminStream(
  sessionId: string,
): Promise<{ ok: true; killed: boolean }> {
  return apiFetch<{ ok: true; killed: boolean }>(
    `/api/super-admin/streams/${encodeURIComponent(sessionId)}/kill`,
    { method: "POST", scope: false },
  );
}

/** `DELETE /api/super-admin/streams/blocks` — clear a kill-block early. */
export function unblockSuperAdminStream(
  body: UnblockStreamRequest,
): Promise<{ ok: true; cleared: boolean }> {
  return apiFetch<{ ok: true; cleared: boolean }>(
    "/api/super-admin/streams/blocks",
    { method: "DELETE", body, scope: false },
  );
}

/** `GET /api/super-admin/live/config` — full live config (key + RTMP URL). */
export function getSuperAdminLiveConfig(): Promise<AdminLiveConfig> {
  return apiFetch<AdminLiveConfig>("/api/super-admin/live/config", {
    scope: false,
  });
}

/** `GET /api/super-admin/live/diagnostics` — MediaMTX ingest snapshot. */
export function getSuperAdminLiveDiagnostics(): Promise<AdminLiveDiagnostics> {
  return apiFetch<AdminLiveDiagnostics>("/api/super-admin/live/diagnostics", {
    scope: false,
  });
}

/** `GET /api/super-admin/matches/{match_id}/errors` — slot error log. */
export function getSuperAdminMatchErrors(
  matchId: string,
): Promise<{ errors: Array<Record<string, unknown>> }> {
  return apiFetch<{ errors: Array<Record<string, unknown>> }>(
    `/api/super-admin/matches/${encodeURIComponent(matchId)}/errors`,
    { scope: false },
  );
}

/** `POST /api/super-admin/matches/{match_id}/slots/{slot}/regenerate-hls`
 * — rebuild the HLS ladder from the existing MP4. */
export function regenerateSuperAdminHls(
  matchId: string,
  slot: string,
): Promise<{
  ok: true;
  match_id: string;
  slot: string;
  regen_in_flight: true;
}> {
  return apiFetch(
    `/api/super-admin/matches/${encodeURIComponent(matchId)}/slots/${encodeURIComponent(slot)}/regenerate-hls`,
    { method: "POST", scope: false },
  );
}

/** `GET /api/super-admin/matches/{match_id}/verify` — per-slot asset
 * integrity report. */
export function verifySuperAdminMatch(matchId: string): Promise<{
  match_id: string;
  slots: Record<string, { mp4: boolean; hls: boolean }>;
  thumbnail: boolean;
  issues: string[];
}> {
  return apiFetch(
    `/api/super-admin/matches/${encodeURIComponent(matchId)}/verify`,
    { scope: false },
  );
}

/** `GET /api/users` — admin user list. */
export function getAdminUsers(): Promise<AdminUser[]> {
  return apiFetch<AdminUser[]>("/api/users", { scope: false });
}

// ---------------------------------------------------------------------------
// M8b — Admin Tenants verbs
// ---------------------------------------------------------------------------

/** `GET /api/admin/teams` — club-host team list, apex global-admin list. */
export function getAdminTeams(): Promise<AdminTeamRow[]> {
  return apiFetch<AdminTeamRow[]>("/api/admin/teams", { scope: false });
}

/** `POST /api/admin/teams` — create team (club-admin on club host, global admin on apex). */
export function createAdminTeam(
  body: CreateAdminTeamRequest,
): Promise<AdminTeamRow> {
  return apiFetch<AdminTeamRow>("/api/admin/teams", {
    method: "POST",
    body,
    scope: false,
  });
}

/** `PATCH /api/admin/teams/{id}` — rename / change game_format. */
export function updateAdminTeam(
  teamId: string,
  body: UpdateAdminTeamRequest,
): Promise<AdminTeamRow> {
  return apiFetch<AdminTeamRow>(
    `/api/admin/teams/${encodeURIComponent(teamId)}`,
    { method: "PATCH", body, scope: false },
  );
}

/** `GET /api/admin/seasons` — the global, shared season list. */
export function getAdminSeasons(): Promise<Season[]> {
  return apiFetch<Season[]>(`/api/admin/seasons`, { scope: false });
}

/** `POST /api/admin/seasons` — create a globally-shared season. */
export function createAdminSeason(
  body: CreateAdminSeasonRequest,
): Promise<Season> {
  return apiFetch<Season>(`/api/admin/seasons`, {
    method: "POST",
    body,
    scope: false,
  });
}

/** `PATCH /api/admin/seasons/{id}` — edit a shared season. */
export function updateAdminSeason(
  seasonId: string,
  body: UpdateAdminSeasonRequest,
): Promise<Season> {
  return apiFetch<Season>(
    `/api/admin/seasons/${encodeURIComponent(seasonId)}`,
    {
      method: "PATCH",
      body,
      scope: false,
    },
  );
}

/** `DELETE /api/admin/seasons/{id}` — remove a shared season. Rejected
 * with 409 by the backend if any match still references it. */
export function deleteAdminSeason(seasonId: string): Promise<Season> {
  return apiFetch<Season>(
    `/api/admin/seasons/${encodeURIComponent(seasonId)}`,
    {
      method: "DELETE",
      scope: false,
    },
  );
}

/** `POST /api/admin/seasons/{id}/default` — pin a season as the
 * system-wide default. The backend clears the flag on every other
 * season so exactly one is the default. */
export function setDefaultAdminSeason(seasonId: string): Promise<Season> {
  return apiFetch<Season>(
    `/api/admin/seasons/${encodeURIComponent(seasonId)}/default`,
    { method: "POST", scope: false },
  );
}

// #908 — season teams (plan §6). Same dual gate as the other admin verbs:
// club host → club_admin, apex → super admin.

/** `POST /api/admin/teams/{id}/carry-forward` — a NEW team in `season_id`
 * carried forward from this one (§6 step 2): lineage pointer, copied
 * settings / branding, a NEW stream key, no staff memberships and no roster
 * rows. `source_roster` is the source team's active assignments,
 * pre-selected for `createRosterAssignmentsBulk` — nothing is rostered by
 * this call. `name` / `slug` default to the source name and
 * `<source-slug>-<season>`. */
export function carryForwardAdminTeam(
  teamId: string,
  body: CarryForwardTeamRequest,
): Promise<CarryForwardTeamResult> {
  return apiFetch<CarryForwardTeamResult>(
    `/api/admin/teams/${encodeURIComponent(teamId)}/carry-forward`,
    { method: "POST", body, scope: false },
  );
}

/** `POST /api/admin/teams/{id}/archive` — archive a season team
 * (idempotent). Nothing is deleted and no access is removed (D5); archived
 * teams leave the live list and the scope pickers' current section. */
export function archiveAdminTeam(teamId: string): Promise<AdminTeamRow> {
  return apiFetch<AdminTeamRow>(
    `/api/admin/teams/${encodeURIComponent(teamId)}/archive`,
    { method: "POST", scope: false },
  );
}

/** `POST /api/admin/seasons/{id}/close` — close a season: every team of the
 * season is archived (idempotent; `archived_team_ids` lists them). Frees the
 * club's team quota (D13). */
export function closeAdminSeason(seasonId: string): Promise<CloseSeasonResult> {
  return apiFetch<CloseSeasonResult>(
    `/api/admin/seasons/${encodeURIComponent(seasonId)}/close`,
    { method: "POST", scope: false },
  );
}

/** `GET /api/admin/teams/{id}/memberships` — memberships list
 * (includes username + display_name from the user join). */
export function getAdminTeamMemberships(
  teamId: string,
): Promise<AdminTeamMembership[]> {
  return apiFetch<AdminTeamMembership[]>(
    `/api/admin/teams/${encodeURIComponent(teamId)}/memberships`,
    { scope: false },
  );
}

/** `POST /api/admin/teams/{id}/memberships` — grant membership. */
export function grantAdminTeamMembership(
  teamId: string,
  body: CreateAdminMembershipRequest,
): Promise<AdminTeamMembership> {
  return apiFetch<AdminTeamMembership>(
    `/api/admin/teams/${encodeURIComponent(teamId)}/memberships`,
    { method: "POST", body, scope: false },
  );
}

/** `DELETE /api/admin/teams/{id}/memberships/{membership_id}` — revoke. */
export function revokeAdminTeamMembership(
  teamId: string,
  membershipId: number | string,
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(
    `/api/admin/teams/${encodeURIComponent(teamId)}/memberships/${encodeURIComponent(String(membershipId))}`,
    { method: "DELETE", scope: false },
  );
}

/** `GET /api/team/memberships?team_id=` — membership-scoped people
 * console. Same response shape as the global-admin variant. */
export function getTeamMemberships(
  teamId: string,
): Promise<AdminTeamMembership[]> {
  return apiFetch<AdminTeamMembership[]>("/api/team/memberships", {
    scope: false,
    query: { team_id: teamId },
  });
}

/** `POST /api/team/memberships` — grant via the membership-scoped
 * route (used by `/admin/people` when a team admin promotes/
 * downgrades — implemented as revoke + re-grant). */
export function grantTeamMembership(
  body: TeamMembershipRequest,
): Promise<AdminTeamMembership> {
  return apiFetch<AdminTeamMembership>("/api/team/memberships", {
    method: "POST",
    body,
    scope: false,
  });
}

/** `DELETE /api/team/memberships/{membership_id}?team_id=` —
 * revoke via the membership-scoped route. */
export function revokeTeamMembership(
  membershipId: number | string,
  teamId: string,
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(
    `/api/team/memberships/${encodeURIComponent(String(membershipId))}`,
    { method: "DELETE", scope: false, query: { team_id: teamId } },
  );
}

/** `GET /api/team/invites?team_id=` — pending + historical
 * invites for the team. */
export function getTeamInvites(teamId: string): Promise<TeamInviteRow[]> {
  return apiFetch<TeamInviteRow[]>("/api/team/invites", {
    scope: false,
    query: { team_id: teamId },
  });
}

/** `POST /api/team/invites` — send a new invite. Response includes
 * `invite_token` only when dev-token delivery is enabled. */
export function createTeamInvite(
  body: CreateTeamInviteRequest,
): Promise<CreateTeamInviteResponse> {
  return apiFetch<CreateTeamInviteResponse>("/api/team/invites", {
    method: "POST",
    body,
    scope: false,
  });
}

/** `POST /api/team/invites/{id}/resend?team_id=` — refresh the
 * token + delivery. Response carries the refreshed row (and
 * `invite_token` on dev-token deliveries). */
export function resendTeamInvite(
  inviteId: string,
  teamId: string,
): Promise<TeamInviteRow> {
  return apiFetch<TeamInviteRow>(
    `/api/team/invites/${encodeURIComponent(inviteId)}/resend`,
    { method: "POST", scope: false, query: { team_id: teamId } },
  );
}

/** `POST /api/team/invites/{id}/revoke?team_id=`. */
export function revokeTeamInvite(
  inviteId: string,
  teamId: string,
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(
    `/api/team/invites/${encodeURIComponent(inviteId)}/revoke`,
    { method: "POST", scope: false, query: { team_id: teamId } },
  );
}

// ---------------------------------------------------------------------------
// M8c — Admin Platform verbs (Users + Settings + Email read)
// ---------------------------------------------------------------------------

/** `GET /api/users/{user_id}` — user-detail drawer payload. */
export function getAdminUserDetail(userId: string): Promise<AdminUserDetail> {
  return apiFetch<AdminUserDetail>(`/api/users/${encodeURIComponent(userId)}`, {
    scope: false,
  });
}

/** `POST /api/users` — create operator account. */
export function createAdminUser(body: CreateUserRequest): Promise<{
  ok: true;
  user: AdminUser;
}> {
  return apiFetch<{ ok: true; user: AdminUser }>("/api/users", {
    method: "POST",
    body,
    scope: false,
  });
}

/** `POST /api/club/users` — club-admin direct account creation on a
 * club host: a plain viewer account + a team membership, atomically.
 * The team is host-club-scoped by the server. */
export function createClubUser(
  body: CreateClubUserRequest,
): Promise<CreateClubUserResponse> {
  return apiFetch<CreateClubUserResponse>("/api/club/users", {
    method: "POST",
    body,
    scope: false,
  });
}

// --- Host-club co-admin delegation (role-model 2b) --------------------------
// Distinct from the super-admin `grantClubAdmin`/`inviteClubAdmin` above (which
// take a clubId and provision the FIRST admin from the apex host): these are
// club-host-gated — the server derives the club from the host, so no clubId
// param — and let a club admin self-govern their own club's admins.

/** `GET /api/club/admins` — the host club's current club admins. */
export function getHostClubAdmins(): Promise<{ admins: ClubAdmin[] }> {
  return apiFetch<{ admins: ClubAdmin[] }>("/api/club/admins", {
    scope: false,
  });
}

/** `GET /api/club/admins/candidates` — host-club members not already admins
 * (the promote picker's source). */
export function getHostClubAdminCandidates(): Promise<{
  candidates: ClubMemberCandidate[];
}> {
  return apiFetch<{ candidates: ClubMemberCandidate[] }>(
    "/api/club/admins/candidates",
    { scope: false },
  );
}

/** `POST /api/club/admins` — grant club_admin to an existing host-club member. */
export function grantHostClubAdmin(userId: string): Promise<{
  club_id: string;
  user_id: string;
  role: string;
}> {
  return apiFetch("/api/club/admins", {
    method: "POST",
    body: { user_id: userId },
    scope: false,
  });
}

/** `POST /api/club/admins/invite` — email a new club-admin invite for the
 * host club. */
export function inviteHostClubAdmin(email: string): Promise<ClubAdminInvite> {
  return apiFetch<ClubAdminInvite>("/api/club/admins/invite", {
    method: "POST",
    body: { email },
    scope: false,
  });
}

/** `DELETE /api/club/admins/{user_id}` — demote a co-admin. 409
 * `last_club_admin` when it would leave the club with no admin. */
export function revokeHostClubAdmin(userId: string): Promise<{
  club_id: string;
  user_id: string;
  revoked: string;
}> {
  return apiFetch(`/api/club/admins/${encodeURIComponent(userId)}`, {
    method: "DELETE",
    scope: false,
  });
}

/** `PATCH /api/users/{user_id}` — update display_name / role /
 * enabled / password reset. Returns `{ ok: true, user }` on
 * success; 409 `last_admin` if the change would lock out the
 * last enabled global admin. */
export function updateAdminUser(
  userId: string,
  body: UpdateUserRequest,
): Promise<{ ok: true; user?: AdminUser }> {
  return apiFetch<{ ok: true; user?: AdminUser }>(
    `/api/users/${encodeURIComponent(userId)}`,
    { method: "PATCH", body, scope: false },
  );
}

/** `DELETE /api/users/{user_id}` — delete operator account everywhere.
 * Deletes linked external identities first; 409 `last_admin` if it's the last
 * enabled global admin. */
export function deleteAdminUser(
  userId: string,
  body: DeleteAdminUserRequest,
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/users/${encodeURIComponent(userId)}`, {
    method: "DELETE",
    body,
    scope: false,
  });
}

/** `GET /api/admin/email/settings` — email config read.
 * Privacy: response NEVER contains raw Brevo API/SMTP keys —
 * status booleans only. */
export function getAdminEmailSettings(): Promise<AdminEmailPayload> {
  return apiFetch<AdminEmailPayload>("/api/admin/email/settings", {
    scope: false,
  });
}

/** `PATCH /api/admin/email/settings` — edit email delivery config.
 * Privacy: Brevo keys are write-only; responses only contain status booleans. */
export function updateAdminEmailSettings(
  body: AdminEmailUpdateRequest,
): Promise<AdminEmailPayload> {
  return apiFetch<AdminEmailPayload>("/api/admin/email/settings", {
    method: "PATCH",
    body,
    scope: false,
  });
}

/** `GET /api/admin/auth-providers/settings` — Google/Apple OIDC config read.
 * Privacy: raw provider secrets are never returned — status booleans only. */
export function getAdminAuthProvidersSettings(): Promise<AdminAuthProvidersPayload> {
  return apiFetch<AdminAuthProvidersPayload>(
    "/api/admin/auth-providers/settings",
    { scope: false },
  );
}

/** `PATCH /api/admin/auth-providers/settings` — edit Google/Apple provider config.
 * Privacy: client secrets and private keys are write-only. */
export function updateAdminAuthProvidersSettings(
  body: AdminAuthProvidersUpdateRequest,
): Promise<AdminAuthProvidersPayload> {
  return apiFetch<AdminAuthProvidersPayload>(
    "/api/admin/auth-providers/settings",
    { method: "PATCH", body, scope: false },
  );
}

/** `POST /api/admin/email/test` — send a one-off delivery probe. */
export function sendAdminEmailTest(
  body: AdminEmailTestRequest,
): Promise<AdminEmailTestResponse> {
  return apiFetch<AdminEmailTestResponse>("/api/admin/email/test", {
    method: "POST",
    body,
    scope: false,
  });
}

/** `POST /api/admin/settings/asset?kind=<club_logo|favicon>` —
 * multipart upload. Bypasses `apiFetch` because the body is FormData,
 * not JSON. Returns the updated settings + assets payload.
 *
 * NOTE: the app logo (mark + wordmark) is the bundled `tktic` brand
 * served from `web/public/brand/` and is not operator-uploadable.
 */
export async function uploadAdminSettingsAsset(
  kind: "club_logo" | "favicon",
  file: File,
): Promise<{
  ok: true;
  kind: string;
  filename: string;
  settings: Record<string, unknown>;
  assets: {
    club_logo_url?: string | null;
    favicon_url?: string | null;
  };
}> {
  const token = getAuthTokenForRequest();
  const form = new FormData();
  form.append("file", file);
  const url = `/api/admin/settings/asset?kind=${encodeURIComponent(kind)}`;
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...csrfHeaderForRequest("POST"),
      ...devClubOverrideHeader(),
    },
    body: form,
  });
  const text = await res.text();
  let parsed: unknown = text;
  try {
    parsed = JSON.parse(text);
  } catch {
    /* leave as text */
  }
  if (!res.ok) {
    let detail: unknown = parsed;
    if (
      parsed &&
      typeof parsed === "object" &&
      "detail" in (parsed as Record<string, unknown>)
    ) {
      detail = (parsed as Record<string, unknown>).detail;
    }
    throw new ApiError(res.status, detail);
  }
  return parsed as Awaited<ReturnType<typeof uploadAdminSettingsAsset>>;
}

/** `POST /api/club/settings/asset?kind=<club_logo|favicon>` — per-club
 * branding upload (club-admin, host-club scoped). Bypasses `apiFetch`
 * because the body is FormData, not JSON. Uploading a `club_logo` also
 * derives the club favicon from it server-side unless the club uploaded an
 * explicit favicon. Same response shape as `uploadAdminSettingsAsset`.
 */
export async function uploadClubSettingsAsset(
  kind: "club_logo" | "favicon",
  file: File,
): Promise<{
  ok: true;
  kind: string;
  filename: string;
  settings: Record<string, unknown>;
  assets: {
    club_logo_url?: string | null;
    favicon_url?: string | null;
  };
}> {
  const token = getAuthTokenForRequest();
  const form = new FormData();
  form.append("file", file);
  const url = `/api/club/settings/asset?kind=${encodeURIComponent(kind)}`;
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...csrfHeaderForRequest("POST"),
      ...devClubOverrideHeader(),
    },
    body: form,
  });
  const text = await res.text();
  let parsed: unknown = text;
  try {
    parsed = JSON.parse(text);
  } catch {
    /* leave as text */
  }
  if (!res.ok) {
    let detail: unknown = parsed;
    if (
      parsed &&
      typeof parsed === "object" &&
      "detail" in (parsed as Record<string, unknown>)
    ) {
      detail = (parsed as Record<string, unknown>).detail;
    }
    throw new ApiError(res.status, detail);
  }
  return parsed as Awaited<ReturnType<typeof uploadClubSettingsAsset>>;
}

/** `POST /api/matches/{match_id}/upload-opponent-logo` — multipart
 * upload of the OPPONENT'S logo for a single match. The tenant club's
 * mark always comes from `settings.club_logo_url` (one club per install),
 * so only the other side ever needs a per-match upload. Same FormData
 * shape as `uploadAdminSettingsAsset`. Accepted formats: .png, .jpg,
 * .jpeg, .svg, .webp; 20 MB cap (server-enforced).
 */
export async function uploadMatchOpponentLogo(
  matchId: string,
  file: File,
): Promise<{ ok: true; filename: string }> {
  const token = getAuthTokenForRequest();
  const form = new FormData();
  form.append("file", file);
  // Active-scope params let `_resolve_match_write_scope` resolve the
  // caller's team (mirrors `apiFetch`'s scope injection — the multipart
  // path bypasses apiFetch because the body is FormData, not JSON).
  const { teamId, seasonId } = currentScope();
  const params = new URLSearchParams();
  if (teamId) params.set("team_id", teamId);
  if (seasonId) params.set("season_id", seasonId);
  const qs = params.toString();
  const url = `/api/matches/${encodeURIComponent(matchId)}/upload-opponent-logo${qs ? `?${qs}` : ""}`;
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...csrfHeaderForRequest("POST"),
      ...devClubOverrideHeader(),
    },
    body: form,
  });
  const text = await res.text();
  let parsed: unknown = text;
  try {
    parsed = JSON.parse(text);
  } catch {
    /* leave as text */
  }
  if (!res.ok) {
    let detail: unknown = parsed;
    if (
      parsed &&
      typeof parsed === "object" &&
      "detail" in (parsed as Record<string, unknown>)
    ) {
      detail = (parsed as Record<string, unknown>).detail;
    }
    throw new ApiError(res.status, detail);
  }
  return parsed as Awaited<ReturnType<typeof uploadMatchOpponentLogo>>;
}

// ---------------------------------------------------------------------------
// M9a — Account self-service verbs
// ---------------------------------------------------------------------------

/** `POST /api/login` — issues a fresh token. Caller is responsible
 * for persisting the token via `setAuthToken` (the LoginPage does
 * this on success). Anonymous endpoint, scope-injection irrelevant. */
export function login(body: LoginRequest): Promise<LoginResponse> {
  return apiFetch<LoginResponse>("/api/login", {
    method: "POST",
    body,
    scope: false,
  });
}

/** `POST /api/register` — self-service account creation. The user
 * starts as a platform viewer with no team membership. */
export function register(body: RegisterRequest): Promise<RegisterResponse> {
  return apiFetch<RegisterResponse>("/api/register", {
    method: "POST",
    body,
    scope: false,
  });
}

/** `GET /api/auth/mode` — anonymous rollout flags for choosing
 * the Kratos panels vs the legacy username/password forms. */
export function getAuthMode(): Promise<AuthModePayload> {
  return apiFetch<AuthModePayload>("/api/auth/mode", { scope: false }).then(
    rememberAuthMode,
  );
}

/** `GET /api/me/auth-identities` — read-only sign-in method summary.
 * The backend intentionally omits provider subjects and raw metadata. */
export function getMeAuthIdentities(): Promise<AuthIdentitiesPayload> {
  return apiFetch<AuthIdentitiesPayload>("/api/me/auth-identities", {
    scope: false,
  });
}

/** `GET /api/me/security` — current user's MFA/passkey policy plus
 * passkey summaries from the Kratos admin API. */
export function getMeSecurity(): Promise<MeSecurityPayload> {
  return apiFetch<MeSecurityPayload>("/api/me/security", { scope: false });
}

/** `PATCH /api/me/security` — update the user's optional MFA preference.
 * Role-required MFA cannot be disabled by the user. */
export function patchMeSecurity(
  body: PatchMeSecurityRequest,
): Promise<PatchMeSecurityResponse> {
  return apiFetch<PatchMeSecurityResponse>("/api/me/security", {
    method: "PATCH",
    body,
    scope: false,
  });
}

/** `GET /api/me/trusted-devices` — active 30-day MFA "remember this
 * device" trusts; `current` marks the browser in use. */
export function getMeTrustedDevices(): Promise<{
  devices: TrustedDeviceSummary[];
}> {
  return apiFetch<{ devices: TrustedDeviceSummary[] }>(
    "/api/me/trusted-devices",
    {
      scope: false,
    },
  );
}

/** `DELETE /api/me/trusted-devices/{id}` — rescind one device trust;
 * that browser gets the second-factor prompt again at next sign-in. */
export function revokeMeTrustedDevice(
  deviceId: string,
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(
    `/api/me/trusted-devices/${encodeURIComponent(deviceId)}`,
    { method: "DELETE", scope: false },
  );
}

/** `POST /api/logout` — clears the server-side session. Caller still
 * needs to apply the local logout boundary. */
export function logout(): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>("/api/logout", {
    method: "POST",
    scope: false,
  });
}

/** `POST /api/me/sessions/revoke-all` — revokes every Tktic session
 * for the current user, including the current browser. Caller should
 * apply the logout boundary immediately after success. */
export function revokeAllMeSessions(): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>("/api/me/sessions/revoke-all", {
    method: "POST",
    scope: false,
  });
}

/** `PATCH /api/me/profile` — Profile-tab save. */
export function patchMeProfile(
  body: PatchMeProfileRequest,
): Promise<{ profile: UserProfile }> {
  return apiFetch<{ profile: UserProfile }>("/api/me/profile", {
    method: "PATCH",
    body,
    scope: false,
  });
}

export function uploadMeProfilePhoto(
  file: File,
): Promise<{ ok: true; profile: UserProfile }> {
  const form = new FormData();
  form.set("file", file);
  return apiFormFetch<{ ok: true; profile: UserProfile }>(
    "/api/me/profile-photo",
    form,
    { scope: false },
  );
}

export function removeMeProfilePhoto(): Promise<{
  ok: true;
  profile: UserProfile;
}> {
  return apiFetch<{ ok: true; profile: UserProfile }>("/api/me/profile-photo", {
    method: "DELETE",
    scope: false,
  });
}

/** `POST /api/me/password` — Password-tab save. Backend revokes
 * all other sessions of the user on success (the auth.py
 * invariant — same behavior as a `last_admin` reset). */
export function changeMePassword(
  body: PasswordChangeRequest,
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>("/api/me/password", {
    method: "POST",
    body,
    scope: false,
  });
}

/** `POST /api/me/email-verification/request` — Email-tab Request
 * button. Returns `{ok, verification_token?}` — the dev-token
 * appears only when `TKTIC_DEV_TOKEN_DELIVERY=1`. */
export function requestMeEmailVerification(): Promise<EmailVerificationRequestResponse> {
  return apiFetch<EmailVerificationRequestResponse>(
    "/api/me/email-verification/request",
    { method: "POST", scope: false },
  );
}

/** `POST /api/me/email-verification/confirm` — anonymous endpoint
 * powering both the Email-tab Confirm action and the
 * `/verify-email?token=` landing. */
export function confirmEmailVerification(
  body: EmailVerificationConfirmRequest,
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>("/api/me/email-verification/confirm", {
    method: "POST",
    body,
    scope: false,
  });
}

/** `POST /api/auth/password-reset/request` — login footer link
 * modal. Generic response (enumeration-safe). Returns
 * `{ok, reset_token?}` — dev-token gate same as email request. */
export function requestPasswordReset(
  body: PasswordResetRequestRequest,
): Promise<PasswordResetRequestResponse> {
  return apiFetch<PasswordResetRequestResponse>(
    "/api/auth/password-reset/request",
    { method: "POST", body, scope: false },
  );
}

/** `POST /api/auth/password-reset/confirm` — `/reset-password?token=`
 * landing. Token-as-auth (≥16 chars). 400 on invalid/expired. */
export function confirmPasswordReset(
  body: PasswordResetConfirmRequest,
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>("/api/auth/password-reset/confirm", {
    method: "POST",
    body,
    scope: false,
  });
}

// M9b — invite acceptance verb (anonymous endpoint).

/** `POST /api/team/invites/accept` — the `/invite/{token}` landing.
 * Two payload shapes:
 *   - Signed-in: `{token}` only — server reads the actor from
 *     the Bearer header.
 *   - Create-account: `{token, username, password, display_name?}`
 *     — server creates the user + signs them in (caller still
 *     needs to follow up with `POST /api/login` to obtain the
 *     fresh token; this endpoint does NOT return one).
 * 404 = unknown token; 409 = already accepted; 410 = expired. */
export function acceptTeamInvite(
  body: AcceptTeamInviteRequest,
): Promise<AcceptTeamInviteResponse> {
  return apiFetch<AcceptTeamInviteResponse>("/api/team/invites/accept", {
    method: "POST",
    body,
    scope: false,
  });
}

/** Pre-accept descriptor for the invite landing page (Phase 2 consent
 * step). POST so the token rides the body, never a query string. */
export function getTeamInviteDescriptor(
  token: string,
): Promise<InviteDescriptor> {
  return apiFetch<InviteDescriptor>("/api/team/invites/descriptor", {
    method: "POST",
    body: { token },
    scope: false,
  });
}

/** Guest share links (Phase 4) — coaching staff mint/list/revoke;
 * the raw token appears only in the create response. */
export function createTeamShareLink(
  teamId: string,
  options:
    | number
    | {
        label?: string | null;
        ttl_hours?: number;
        expires_at?: number;
        no_expiry?: boolean;
      },
): Promise<CreatedShareLink> {
  return apiFetch<CreatedShareLink>(
    `/api/teams/${encodeURIComponent(teamId)}/share-links`,
    {
      method: "POST",
      body: typeof options === "number" ? { ttl_hours: options } : options,
      scope: false,
    },
  );
}

export function listTeamShareLinks(
  teamId: string,
): Promise<{ links: ShareLink[] }> {
  return apiFetch<{ links: ShareLink[] }>(
    `/api/teams/${encodeURIComponent(teamId)}/share-links`,
    { scope: false },
  );
}

export function listClubShareLinks(): Promise<{ links: ClubShareLink[] }> {
  return apiFetch<{ links: ClubShareLink[] }>("/api/teams/share-links", {
    scope: false,
  });
}

export function revokeTeamShareLink(
  teamId: string,
  linkId: string,
): Promise<ShareLink> {
  return apiFetch<ShareLink>(
    `/api/teams/${encodeURIComponent(teamId)}/share-links/${encodeURIComponent(linkId)}/revoke`,
    { method: "POST", scope: false },
  );
}

/** Redeem a guest link — anonymous; sets the HttpOnly guest cookie. */
export function claimGuestLink(token: string): Promise<GuestClaimResponse> {
  return apiFetch<GuestClaimResponse>("/api/guest/claim", {
    method: "POST",
    body: { token },
    scope: false,
  });
}

export function getAccessRequestOptions(
  teamId?: string,
): Promise<AccessRequestOptions> {
  return apiFetch<AccessRequestOptions>("/api/access-requests/options", {
    scope: false,
    query: { team_id: teamId || undefined },
  });
}

export function createAccessRequest(
  body: CreateAccessRequestBody,
): Promise<AccessRequestRow> {
  return apiFetch<AccessRequestRow>("/api/access-requests", {
    method: "POST",
    body,
    scope: false,
  });
}

export function getMyAccessRequests(): Promise<AccessRequestRow[]> {
  return apiFetch<AccessRequestRow[]>("/api/me/access-requests", {
    scope: false,
  });
}

export function cancelAccessRequest(id: string): Promise<AccessRequestRow> {
  return apiFetch<AccessRequestRow>(
    `/api/access-requests/${encodeURIComponent(id)}/cancel`,
    { method: "POST", scope: false },
  );
}

export function getAdminAccessRequests(): Promise<AccessRequestRow[]> {
  return apiFetch<AccessRequestRow[]>("/api/admin/access-requests", {
    scope: false,
  });
}

export function approveAccessRequest(
  id: string,
  resolutionNote = "",
): Promise<AccessRequestRow> {
  return apiFetch<AccessRequestRow>(
    `/api/admin/access-requests/${encodeURIComponent(id)}/approve`,
    { method: "POST", body: { resolution_note: resolutionNote }, scope: false },
  );
}

export function rejectAccessRequest(
  id: string,
  resolutionNote = "",
): Promise<AccessRequestRow> {
  return apiFetch<AccessRequestRow>(
    `/api/admin/access-requests/${encodeURIComponent(id)}/reject`,
    { method: "POST", body: { resolution_note: resolutionNote }, scope: false },
  );
}

// ---------------------------------------------------------------------------
// Consent visibility (docs/plans/consent-visibility-plan.md)
// ---------------------------------------------------------------------------

/** `GET /api/me/consents` — the caller's OWN consent history plus the
 * re-consent work list. Host-agnostic like the other /api/me routes. */
export function getMyConsents(): Promise<MyConsentsPayload> {
  return apiFetch<MyConsentsPayload>("/api/me/consents", { scope: false });
}

/** `POST /api/me/consents/reaffirm` — write fresh current-version rows
 * for an existing family membership (after a DOC_VERSION bump, or first
 * consent for a pre-capture membership). */
export function reaffirmMyConsents(
  teamId: string,
  consents: InviteConsents,
  expectedPlayerIds?: string[],
): Promise<{ ok: boolean; team_id: string; players: number }> {
  return apiFetch("/api/me/consents/reaffirm", {
    method: "POST",
    // `expected_player_ids` pins the children the form NAMED. The server
    // recomputes the real list and 409s on a mismatch, so a child linked
    // while the panel was open can't be silently stamped as consented.
    body: {
      team_id: teamId,
      consents,
      ...(expectedPlayerIds ? { expected_player_ids: expectedPlayerIds } : {}),
    },
    scope: false,
  });
}

/** `POST /api/me/consents/withdrawals` — records a withdrawal REQUEST
 * for staff to action; deletes nothing itself. */
export function createConsentWithdrawal(body: {
  team_id: string;
  player_id?: string | null;
  message?: string;
}): Promise<{
  id: string;
  status: string;
  created_at: string;
  already_pending: boolean;
}> {
  return apiFetch("/api/me/consents/withdrawals", {
    method: "POST",
    body,
    scope: false,
  });
}

/** `GET /api/admin/teams/{id}/consents` — staff evidence view
 * (roster:manage): per-player status/who/when/version/source plus the
 * withdrawal queue. Never carries the operator's note. */
export function getAdminTeamConsents(
  teamId: string,
): Promise<TeamConsentOverview> {
  return apiFetch<TeamConsentOverview>(
    `/api/admin/teams/${encodeURIComponent(teamId)}/consents`,
    { scope: false },
  );
}

/** `POST /api/admin/teams/{id}/consent-withdrawals/{wid}/resolve` —
 * mark a withdrawal handled after doing the actual work. */
export function resolveConsentWithdrawal(
  teamId: string,
  withdrawalId: string,
  resolutionNote = "",
): Promise<{ id: string; status: string; resolution_note?: string }> {
  return apiFetch(
    `/api/admin/teams/${encodeURIComponent(teamId)}/consent-withdrawals/${encodeURIComponent(withdrawalId)}/resolve`,
    {
      method: "POST",
      body: { resolution_note: resolutionNote },
      scope: false,
    },
  );
}

export function getTournaments(
  teamId: string,
  seasonId: string,
): Promise<{ tournaments: import("./types").Tournament[] }> {
  return apiFetch("/api/tournaments", { scope: { teamId, seasonId } });
}
export function getTeamPerformance(
  slug: string,
  seasonId?: string,
): Promise<import("./types").TeamPerformance> {
  return apiFetch(`/api/teams/${encodeURIComponent(slug)}/performance`, {
    scope: false,
    query: { season_id: seasonId },
  });
}
export function getTeamResults(
  slug: string,
  query: {
    season_id?: string;
    q?: string;
    competition?: string;
    tournament_id?: string;
    status?: string;
    side?: string;
    recording?: string;
    result?: string;
    page?: number;
    limit?: number;
  },
): Promise<import("./types").TeamResults> {
  return apiFetch(`/api/teams/${encodeURIComponent(slug)}/results`, {
    scope: false,
    query,
  });
}

export function getAdminMatchLibrary(query: import("./types").AdminMatchLibraryQuery): Promise<import("./types").AdminMatchLibraryResponse> {
  return apiFetch("/api/admin/matches", { query, scope: false });
}
export function transferMatch(id: string, body: import("./types").TransferMatchRequest): Promise<Match> {
  return apiFetch(`/api/matches/${encodeURIComponent(id)}/transfer`, { method: "POST", body, scope: false });
}

/** Resolve a normal note's recording through the existing authorized team list. */
export function getRecordingMatches(teamId: string, signal?: AbortSignal): Promise<Match[]> {
  return allMatchPages({ scope: { teamId, seasonId: null }, signal });
}


export type FeedbackConversationOptions = { scope: Conversation.FeedbackConversationScope; signal?: AbortSignal };

/** A client fence, never a content grant: domain authorization remains server-side. */
export function isCurrentFeedbackConversationScope(scope: Conversation.FeedbackConversationScope): boolean {
  const auth = useAuthStore.getState();
  const current = useScopeStore.getState();
  return !auth.hydrating && auth.user?.id === scope.actorId && auth.currentClub?.id === scope.clubId
    && auth.currentClub.status === 'active' && !current.selectionRequired
    && current.teamId === scope.teamId && current.seasonId === scope.seasonId
    && current.active?.team.id === scope.teamId && (current.active.season?.id ?? null) === scope.seasonId
    && current.active.capabilities.includes(scope.view === 'coach' ? 'coach_object:read' : 'feedback:read_linked');
}

/** Stable observed authority, excluding display/profile labels and object identity. */
export function feedbackConversationAuthorityFingerprint(): string {
  const auth = useAuthStore.getState(); const scope = useScopeStore.getState();
  const membership = scope.active?.membership;
  return JSON.stringify({ actor: auth.user?.id ?? null, club: auth.currentClub?.id ?? null,
    clubStatus: auth.currentClub?.status ?? null, hydrating: auth.hydrating,
    team: scope.teamId, season: scope.seasonId, selectionRequired: scope.selectionRequired,
    effectiveRole: scope.active?.effective_role ?? null,
    capabilities: [...(scope.active?.capabilities ?? [])].sort(),
    membership: membership ? [membership.id, membership.team_id, membership.role, [...membership.capabilities].sort()] : null,
    memberships: auth.memberships.filter(row => row.team_id === scope.teamId)
      .map(row => [String(row.id), row.role, [...row.capabilities].sort()])
      .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))),
    canOpenFeedback: auth.me?.can_open_feedback ?? null,
    tokenGeneration: authTokenGeneration,
  });
}

/** Capture one authority lifetime, including leave-and-return and token replacement. */
export function captureFeedbackConversationBoundary(scope: Conversation.FeedbackConversationScope) {
  const captured = { ...scope };
  const token = getAuthToken();
  const generation = authTokenGeneration;
  const authority = feedbackConversationAuthorityFingerprint();
  let departed = !isCurrentFeedbackConversationScope(captured);
  const observe = () => {
    if (!isCurrentFeedbackConversationScope(captured) || authority !== feedbackConversationAuthorityFingerprint()) departed = true;
  };
  const authOff = useAuthStore.subscribe(observe);
  const scopeOff = useScopeStore.subscribe(observe);
  return {
    current: () => !departed && isCurrentFeedbackConversationScope(captured)
      && authority === feedbackConversationAuthorityFingerprint() && token === getAuthToken() && generation === authTokenGeneration,
    dispose: () => { departed = true; authOff(); scopeOff(); },
  };
}

function conversationPath(target: Conversation.FeedbackConversationTarget): string {
  if (!target.feedbackId || !target.playerId) throw new RangeError('invalid_conversation_target');
  return `/api/feedback/${encodeURIComponent(target.feedbackId)}/players/${encodeURIComponent(target.playerId)}/conversation`;
}

export function isFeedbackConversationContentUnavailable(error: unknown): boolean {
  if (!(error instanceof ApiError) || ![403, 404].includes(error.status)) return false;
  const code = error.detail && typeof error.detail === 'object' && 'code' in error.detail ? error.detail.code : null;
  return code === 'conversation_unavailable' || code === 'conversation_access_required';
}

async function conversationFetch<T>(target: Conversation.FeedbackConversationTarget, suffix: string,
  options: FeedbackConversationOptions, request: Pick<ApiOptions, 'method' | 'body' | 'query'> = {}): Promise<T> {
  const captured = { ...options, scope: { ...options.scope } };
  const boundary = captureFeedbackConversationBoundary(captured.scope);
  const current = () => boundary.current() && !captured.signal?.aborted;
  try {
    if (!current()) throw new DOMException('Conversation context changed', 'AbortError');
    const value = await apiFetch<T>(conversationPath(target) + suffix, {
      ...resourceReadOptions(captured), ...request, query: { ...request.query, view: captured.scope.view },
    });
    if (!current()) throw new DOMException('Conversation context changed', 'AbortError');
    return value;
  } catch (error) {
    if (!current()) throw new DOMException('Conversation context changed', 'AbortError');
    if (isFeedbackConversationContentUnavailable(error)) {
      const cache = await import('./queryClient');
      if (!current()) throw new DOMException('Conversation context changed', 'AbortError');
      await cache.removeUnavailableConversation(cache.feedbackConversationKey(captured.scope, target, 'child'));
    }
    throw error;
  } finally { boundary.dispose(); }
}

export function getFeedbackConversation(target: Conversation.FeedbackConversationTarget,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackConversationResponse> {
  return conversationFetch(target, '', options);
}
export function getFeedbackMessages(target: Conversation.FeedbackConversationTarget, query: Conversation.FeedbackTranscriptQuery,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackTranscriptResponse> {
  return conversationFetch(target, '/messages', options, { query: { limit: query.limit ?? 20, cursor: query.cursor ?? null } });
}
export function getFeedbackAnsweredContext(target: Conversation.FeedbackConversationTarget, messageId: string,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackAnsweredContextResponse> {
  return conversationFetch(target, `/messages/${encodeURIComponent(messageId)}/context`, options);
}

export function getFeedbackConversationReadState(target: Conversation.FeedbackConversationTarget,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackConversationReadState> {
  return conversationFetch(target, '/read-state', options);
}

export function getFeedbackWriting(target: Conversation.FeedbackConversationTarget,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackWritingState> {
  return conversationFetch(target, '/writing', options);
}

export function getFeedbackPendingSend(target: Conversation.FeedbackConversationTarget,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackPendingState> {
  return conversationFetch(target, '/pending-send', options);
}

export function serveFeedbackFullView(target: Conversation.FeedbackConversationTarget, body: Conversation.FeedbackFullViewRequest,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackFullViewResponse> {
  return conversationFetch(target, '/full-view', options, { method: 'POST', body });
}

export function serveFeedbackTranscript(target: Conversation.FeedbackConversationTarget, body: Conversation.FeedbackTranscriptViewRequest,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackTranscriptViewResponse> {
  return conversationFetch(target, '/transcript-view', options, { method: 'POST', body });
}

export function markFeedbackCurrentReviewed(target: Conversation.FeedbackConversationTarget, body: Conversation.FeedbackCurrentReviewRequest,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackCurrentReviewResult> {
  return conversationFetch(target, '/current-review', options, { method: 'PUT', body });
}

export function markFeedbackOwnReading(target: Conversation.FeedbackConversationTarget, body: Conversation.FeedbackOwnReadRequest,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackOwnReadResult> {
  return conversationFetch(target, '/own-reading', options, { method: 'PUT', body });
}

export function markFeedbackConversationSeen(target: Conversation.FeedbackConversationTarget, body: Conversation.FeedbackSeenRequest,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackSeenResult> {
  return conversationFetch(target, '/seen', options, { method: 'PUT', body });
}

export function saveFeedbackWriting(target: Conversation.FeedbackConversationTarget, body: Conversation.SaveFeedbackWritingRequest,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackWritingResult> {
  return conversationFetch(target, '/writing', options, { method: 'PUT', body });
}

export function discardFeedbackWriting(target: Conversation.FeedbackConversationTarget, body: Conversation.DiscardFeedbackWritingRequest,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackWritingResult> {
  return conversationFetch(target, '/writing', options, { method: 'DELETE', body });
}

export function selectFeedbackCurrent(target: Conversation.FeedbackConversationTarget, body: Conversation.SelectFeedbackCurrentRequest,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackWritingResult> {
  return conversationFetch(target, '/selection', options, { method: 'PUT', body });
}

export function reserveFeedbackSend(target: Conversation.FeedbackConversationTarget, body: Conversation.FeedbackSendReservationRequest,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackPendingResult> {
  return conversationFetch(target, '/pending-send', options, { method: 'POST', body });
}

export function executeFeedbackSend(target: Conversation.FeedbackConversationTarget, body: Conversation.FeedbackPendingAttemptRequest,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackPendingResult> {
  return conversationFetch(target, '/pending-send/execution', options, { method: 'POST', body });
}

export function resolveFeedbackSend(target: Conversation.FeedbackConversationTarget, body: Conversation.FeedbackPendingAttemptRequest,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackPendingResult> {
  return conversationFetch(target, '/pending-send/resolution', options, { method: 'POST', body });
}

export function reconcileFeedbackMessage(target: Conversation.FeedbackConversationTarget, body: Conversation.AppendFeedbackMessageRequest,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackMessageResult> {
  return conversationFetch(target, '/messages/reconciliation', options, { method: 'POST', body });
}

export function closeFeedbackConversation(target: Conversation.FeedbackConversationTarget, body: Conversation.FeedbackLifecycleRequest,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackLifecycleResult> {
  return conversationFetch(target, '/close', options, { method: 'POST', body });
}

export function reopenFeedbackConversation(target: Conversation.FeedbackConversationTarget, body: Conversation.FeedbackLifecycleRequest,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackLifecycleResult> {
  return conversationFetch(target, '/reopen', options, { method: 'POST', body });
}

export function requestFeedbackReopening(target: Conversation.FeedbackConversationTarget, body: Conversation.FeedbackReopeningRequest,
  options: FeedbackConversationOptions): Promise<Conversation.FeedbackLifecycleResult> {
  return conversationFetch(target, '/request-reopening', options, { method: 'POST', body });
}

export type RecapOptions = { scope: Conversation.RecapScope; signal?: AbortSignal };

/** Observed lifetime fence only. Recap family access is broader than a capability bit. */
export function isCurrentRecapScope(scope: Conversation.RecapScope): boolean {
  const auth = useAuthStore.getState(); const current = useScopeStore.getState();
  return !auth.hydrating && auth.user?.id === scope.actorId && auth.currentClub?.id === scope.clubId
    && auth.currentClub.status === 'active' && !current.selectionRequired
    && current.teamId === scope.teamId && current.seasonId === scope.seasonId
    && current.active?.team.id === scope.teamId && (current.active.season?.id ?? null) === scope.seasonId
    && (scope.view === 'recipient' || current.active.capabilities.includes('coach_object:read'));
}

export function captureRecapBoundary(scope: Conversation.RecapScope) {
  const captured = { ...scope }; const token = getAuthToken(); const generation = authTokenGeneration;
  const authority = feedbackConversationAuthorityFingerprint();
  let departed = !isCurrentRecapScope(captured);
  const observe = () => {
    if (!isCurrentRecapScope(captured) || authority !== feedbackConversationAuthorityFingerprint()) departed = true;
  };
  const authOff = useAuthStore.subscribe(observe); const scopeOff = useScopeStore.subscribe(observe);
  return {
    current: () => !departed && isCurrentRecapScope(captured) && authority === feedbackConversationAuthorityFingerprint()
      && token === getAuthToken() && generation === authTokenGeneration,
    dispose: () => { departed = true; authOff(); scopeOff(); },
  };
}

export function recapPath(id: string): string {
  if (typeof id !== 'string' || !/^[1-9][0-9]{0,18}$/.test(id) || BigInt(id) > 9223372036854775807n) {
    throw new RangeError('invalid_recap_id');
  }
  return `/api/recaps/${id}`;
}

export function isRecapContentUnavailable(error: unknown): boolean {
  if (!(error instanceof ApiError) || ![403, 404].includes(error.status)) return false;
  const code = error.detail && typeof error.detail === 'object' && 'code' in error.detail ? error.detail.code : null;
  return code === 'recap_unavailable' || code === 'recap_access_required';
}

async function recapFetch<T>(path: string, options: RecapOptions, request: Pick<ApiOptions, 'method' | 'body' | 'query'> = {}): Promise<T> {
  const captured = { ...options, scope: { ...options.scope } }; const boundary = captureRecapBoundary(captured.scope);
  const current = () => boundary.current() && !captured.signal?.aborted;
  try {
    if (!current()) throw new DOMException('Recap context changed', 'AbortError');
    const value = await apiFetch<T>(path, { ...resourceReadOptions(captured), ...request });
    if (!current()) throw new DOMException('Recap context changed', 'AbortError');
    return value;
  } catch (error) {
    if (!current()) throw new DOMException('Recap context changed', 'AbortError');
    // Privacy loss and operation uncertainty are independent. A denied command
    // can invalidate previously readable prose without proving its old outcome.
    if (error instanceof ApiError && (error.status === 401 || isRecapContentUnavailable(error))) {
      const cache = await import('./queryClient');
      if (!current()) throw new DOMException('Recap context changed', 'AbortError');
      if (error.status === 401) cache.applyAuthExpiredBoundary();
      else {
        const id = path.split('/')[3];
        if (id) await cache.removeUnavailableRecap(cache.recapKey(captured.scope, id, 'detail'));
        else if (captured.scope.view === 'coach') await cache.removeCoachingReadPages(cache.coachRecapCollectionKey(captured.scope));
      }
    }
    throw error;
  } finally { boundary.dispose(); }
}

function recapView(options: RecapOptions, view: 'coach' | 'recipient'): void {
  if (options.scope.view !== view) throw new Error('recap_view_required');
}
export function getRecap(id: string, options: RecapOptions): Promise<Conversation.RecapDetail> {
  return recapFetch(recapPath(id), options, { query: { view: options.scope.view } });
}
export function listCoachRecaps(query: Conversation.RecapCollectionQuery,
  options: RecapOptions): Promise<Conversation.RecapCollectionResponse> {
  recapView(options,'coach');
  return recapFetch('/api/recaps',options,{ query:{ view:'coach', ...normalizeCoachRecapQuery(query) } });
}
export function createRecap(body: Conversation.RecapCreateRequest, options: RecapOptions): Promise<Conversation.RecapWriteResult> {
  recapView(options, 'coach'); return recapFetch('/api/recaps', options, { method: 'POST', body });
}
export function editRecap(id: string, body: Conversation.RecapEditRequest, options: RecapOptions): Promise<Conversation.RecapWriteResult> {
  recapView(options, 'coach'); return recapFetch(recapPath(id), options, { method: 'PUT', body });
}
export function deleteRecap(id: string, body: Conversation.RecapDeleteRequest, options: RecapOptions): Promise<Conversation.RecapDeleteResult> {
  recapView(options, 'coach'); return recapFetch(recapPath(id), options, { method: 'DELETE', body });
}
export function getRecapAcknowledgments(id: string, options: RecapOptions): Promise<Conversation.RecapAcknowledgmentsResponse> {
  recapView(options, 'coach'); return recapFetch(recapPath(id) + '/acknowledgments', options);
}
export function setRecapSeen(id: string, body: Conversation.RecapSeenRequest, options: RecapOptions): Promise<Conversation.RecapSeenResult> {
  recapView(options, 'recipient'); return recapFetch(recapPath(id) + '/seen', options, { method: 'PUT', body });
}
export function getRecapOwnReading(id: string, options: RecapOptions): Promise<Conversation.RecapOwnReadingResponse> {
  recapView(options, 'recipient'); return recapFetch(recapPath(id) + '/own-reading', options);
}
export function serveRecapFullView(id: string, body: Conversation.RecapFullViewRequest, options: RecapOptions): Promise<Conversation.RecapFullViewResponse> {
  recapView(options, 'recipient'); return recapFetch(recapPath(id) + '/full-view', options, { method: 'POST', body });
}
export function prepareRecapOwnRead(id: string, body: Conversation.RecapOwnReadProofRequest, options: RecapOptions): Promise<Conversation.RecapOwnReadProofResponse> {
  recapView(options, 'recipient'); return recapFetch(recapPath(id) + '/own-reading-proof', options, { method: 'POST', body });
}
export function markRecapOwnReading(id: string, body: Conversation.RecapOwnReadRequest, options: RecapOptions): Promise<Conversation.RecapOwnReadResult> {
  recapView(options, 'recipient'); return recapFetch(recapPath(id) + '/own-reading', options, { method: 'PUT', body });
}

/** Canonical normalization shared by request serialization and private cache keys. */
export function normalizeFeedbackCollectionQuery(view: 'coach' | 'recipient',
  query: Conversation.CoachFeedbackCollectionQuery | Conversation.RecipientFeedbackCollectionQuery = {}) {
  const common = { search: (query.search ?? '').trim(), player_id: query.player_id ?? null,
    conversation: query.conversation ?? 'all', responder: query.responder ?? 'all',
    sort: query.sort ?? 'recent', limit: query.limit ?? 20, cursor: query.cursor ?? null };
  if (view === 'coach') {
    const coach = query as Conversation.CoachFeedbackCollectionQuery;
    return { ...common, publication: coach.publication ?? 'all', attention: coach.attention ?? 'all' };
  }
  const recipient = query as Conversation.RecipientFeedbackCollectionQuery;
  const quiet = recipient.conversation === 'closed' || recipient.recap_seen === 'seen' || recipient.recap_read === 'read';
  return { ...common, kind: recipient.kind ?? 'all', attention: recipient.attention ?? (quiet ? 'all' : 'attention'),
    feedback_read: recipient.feedback_read ?? 'all', match_id: recipient.match_id ?? null,
    without_match: recipient.without_match ?? false, recap_seen: recipient.recap_seen ?? 'all', recap_read: recipient.recap_read ?? 'all' };
}

async function collectionFetch<T>(view: 'coach' | 'recipient',
  query: Conversation.CoachFeedbackCollectionQuery | Conversation.RecipientFeedbackCollectionQuery,
  options: { scope: Conversation.FeedbackCollectionScope; signal?: AbortSignal }): Promise<T> {
  if (options.scope.view !== view) throw new Error('collection_view_required');
  const captured = { ...options, scope: { ...options.scope } };
  // Recap authority is wider than a linked child; the server still derives
  // each row's actual authority, including for combined-role accounts.
  const boundary = captureRecapBoundary(captured.scope);
  const current = () => boundary.current() && !captured.signal?.aborted;
  try {
    if (!current()) throw new DOMException('Collection context changed', 'AbortError');
    const result = await apiFetch<T>(view === 'coach' ? '/api/coach/feedback' : '/api/feedback',
      { ...resourceReadOptions(captured), query: normalizeFeedbackCollectionQuery(view, query) });
    if (!current()) throw new DOMException('Collection context changed', 'AbortError');
    return result;
  } catch (error) {
    if (!current()) throw new DOMException('Collection context changed', 'AbortError');
    if (error instanceof ApiError && [401, 403, 404, 409].includes(error.status)) {
      const cache = await import('./queryClient');
      if (!current()) throw new DOMException('Collection context changed', 'AbortError');
      if (error.status === 401) cache.applyAuthExpiredBoundary();
      else await cache.removeFeedbackCollections(captured.scope);
    }
    throw error;
  } finally { boundary.dispose(); }
}

export function listCoachFeedback(query: Conversation.CoachFeedbackCollectionQuery,
  options: { scope: Conversation.FeedbackCollectionScope; signal?: AbortSignal }): Promise<Conversation.CoachFeedbackCollectionResponse> {
  return collectionFetch('coach', query, options);
}
export function listRecipientFeedback(query: Conversation.RecipientFeedbackCollectionQuery,
  options: { scope: Conversation.FeedbackCollectionScope; signal?: AbortSignal }): Promise<Conversation.RecipientFeedbackCollectionResponse> {
  return collectionFetch('recipient', query, options);
}

/** `GET /api/feedback/athletes/{id}/collection` — the athlete-first
 * collection (#908 §5.1): the athlete's assignments grouped by season then
 * team, each read through the same per-scope fences as the collections
 * above. The server picks the `view`: `recipient` for an account linked to
 * the athlete (its current team memberships only), else `coach` for the
 * viewer's own teams. An unknown / other-club / unauthorised athlete is a
 * 404 `collection_unavailable` — never a 403 that would confirm existence.
 * Club host only and not team-scoped, so scope injection is suppressed. */
export function getAthleteFeedbackCollection(athleteId: string,
  options: { signal?: AbortSignal } = {}): Promise<FeedbackAthleteCollection> {
  return apiFetch<FeedbackAthleteCollection>(
    `/api/feedback/athletes/${encodeURIComponent(athleteId)}/collection`,
    { scope: false, signal: options.signal });
}

export function getConsentChoices(invitationToken?: string, signal?: AbortSignal) {
  return invitationToken
    ? apiFetch<import('./types').ConsentChoicesCatalogue>('/api/me/consent-choices/catalogue', { method: 'POST', body: { invitation_token: invitationToken }, scope: false, signal })
    : apiFetch<import('./types').ConsentChoicesCatalogue>('/api/me/consent-choices', { scope: false, signal });
}
export function confirmConsentChoices(body: import('./types').ConsentChoicesRequest, signal?: AbortSignal) {
  return apiFetch<import('./types').ConsentChoicesReceipt>('/api/me/consent-choices', { method: 'POST', body, scope: false, signal });
}
export function withdrawConsentChoice(body: import('./types').ConsentChoiceWithdrawalRequest, signal?: AbortSignal) {
  return apiFetch<import('./types').ConsentChoicesCatalogue>('/api/me/consent-choices/withdraw', { method: 'POST', body, scope: false, signal });
}
